import React from "react";
import { api, formatWhen, setNameFor, suggestionNameFor } from "./api.jsx";
import { useApp, useMedia, waitsOnYou } from "./store.jsx";
import { SectionHead, StatRow, ModalHost, LanguageSwitch, CardmarketMark, MarketFollowFields, Linked, BulkBar, Dialog, ConfirmDialog, useConfirm } from "./chrome.jsx";
import { GAMES, KNOWN_GAMES, gameLabel, GRADE_NAMES, CARD_LANGUAGE_NAMES, MEETUP_STATUS_NAMES, shortCount } from "./data.jsx";
/*
 * THE SCREENS A SHOP IS ALLOWED, rendered INSIDE the rail rather than reached
 * by leaving it. Posting a card and the inbox are the two a shop genuinely
 * uses — the owner's list — and the importer comes with them because it is the
 * one a shop actually stocks from, and sending it out of the shell for that
 * would be the same problem one click along.
 */
import { EditListingDialog } from "./you.jsx";
import { PostScreen } from "./post.jsx";
import { InboxScreen } from "./inbox.jsx";
import { TradeActions, CardTraderLine, isPast, useAccepting, endTradeQuestion } from "./trades.jsx";
import { ImportScreen, IMPORT_SOURCES } from "./import.jsx";
import { ListingScreen } from "./listing.jsx";
import { pushOverlayEntry, onOverlayEntry } from "./router.jsx";
import { ShopScreen } from "./shop.jsx";
import { TraderScreen } from "./trader.jsx";
import { CardAutocomplete } from "./autocomplete.jsx";

const {
  Button, IconButton, Icon, Badge, Card, Input, Radio, Tag, Tabs, Wordmark,
  BottomNav, FluteDivider, EmptyState, ConditionChip, SearchField, DistanceTag, MeetupCard, Skeleton,
  Tooltip,
} = window.CardboardmarketDesignSystem_2a3239;

/**
 * THE SHOP'S BACK OFFICE, as designed.
 *
 * The first version of this was a page inside the app's own furniture, on the
 * argument that a shop is an account and acting as it already turns the Selling
 * tab into its stock. The owner looked at it and said it "doesn't look anything
 * like" his design, and he was right — then said why, which is the part worth
 * writing down:
 *
 *   "shops don't even think of wants lists. They are sell-focused and they have
 *    by far much more stock of things than regular people... they need to have
 *    everything aligned with all their bulk actions and stuff and knowing
 *    quickly the orders they have pending, quick ways of accepting them."
 *
 * So a shop's screen is not a trader's screen with a different header. The
 * MODEL is untouched — the same listings, the same trades, the same account —
 * and the VIEW is the shop's: a table where a trader gets a grid, tick boxes
 * and a bulk bar where a trader gets one card at a time, and pending requests
 * with Accept on the row.
 *
 * WHAT IS CUT, all on his word: the counter queue and pickup codes, the trading
 * tables, "alert those traders", the team tab, and anything to do with wants
 * lists — a shop is where other people's wants get bought FROM, which is why
 * the demand figures point at its stock rather than away from it.
 *
 * WHAT IS SUBSTITUTED: the design's "views · saves" column is how many traders
 * have the card on a wants list. Views is a number we do not collect and saves
 * were withdrawn from the product; a want names somebody who would come and get
 * it. His decision, taken before this screen existed.
 */

/** The sections, and the order the rail lists them in. */
const NAV = [
  { value: 'today', label: "Today", icon: 'clock' },
  { value: 'stock', label: "Stock", icon: 'layers' },
  /*
   * `count` names which unseen number belongs to this item. Reported: "for
   * shops I believe we still have the chime and title indicator for unseen
   * messages/trades, but we don't really show a counter or some indication that
   * we have n unseen requests or n unseen messages."
   *
   * The chime and the tab title were doing the whole job, and both of those are
   * gone the moment somebody is looking at the screen — which is most of a
   * shop's day. The rail is where the answer belongs: a shop reads it to decide
   * where to go, and "go where" is exactly the question a count answers.
   *
   * THE SAME TWO NUMBERS THE APP'S OWN NAVIGATION USES, not a second count
   * computed here. `pendingTrades` is trades waiting on YOU — not every open
   * one, which would never reach zero and would stop meaning anything.
   */
  { value: 'requests', label: "Requests", icon: 'arrow-left-right', count: 'trades' },
  /*
   * The inbox, which is the one place a shop talks to a trader. It is a rail
   * item rather than an embedded screen's parent only: pressing it goes to
   * `/inbox`, the ordinary address, and the shell renders the ordinary screen.
   */
  { value: 'inbox', label: "Inbox", icon: 'message-circle', route: 'inbox', count: 'messages' },
  { value: 'shop', label: "The store", icon: 'store' },
];

/* ------------------------------------------------------------------ strip */

/**
 * Which account these screens belong to, said on every screen that is NOT the
 * back office.
 *
 * The shell says it for itself — a dark rail with the shop's name in it is not
 * ambiguous. This is for the rest of the app: somebody acting as a shop who
 * goes to browse is still the shop, and pressing Post a card there does not
 * post their own card.
 */
export function ShopStrip() {
  const { trader, actAs, go, route, t } = useApp();
  if (!trader || trader.kind !== 'shop') return null;
  if (route.name === 'dashboard') return null;
  return (
    <div data-cbm="shop-strip" style={{ background: 'var(--ink-900)', color: 'var(--kraft-50)' }}>
      <div className="cbm-bar__in">
        <Icon name="store" size={16} />
        <span style={{ flex: 1, minWidth: 160, display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <span style={{ font: 'var(--type-meta)', letterSpacing: 'var(--tracking-label)', textTransform: 'uppercase', opacity: .7 }}>
            {t("Shop account")}
          </span>
          <span style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)' }}>{trader.displayName}</span>
        </span>
        <span className="cbm-bar__acts">
          <button type="button" className="cbm-dash__ghost" onClick={() => go('dashboard')}>{t("The counter")}</button>
          <button type="button" className="cbm-dash__ghost" onClick={() => actAs(null, '/')}>
            {t("Switch to trading")}
          </button>
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- scope */

/*
 * THE SCOPE LIVES IN THE ADDRESS AND ON THE DEVICE.
 *
 * In the address as `game`, the key browse already carries, repeated once per
 * game — so "my Magic requests" is a link somebody can send, and a reload keeps
 * it. The ABSENCE of the key is "All games" here. Browse needs a third state
 * (`?game=`, "all games, said out loud") because an absent key there falls
 * back to the games saved on the account; the back office has no such list to
 * fall back on, so it never writes the empty key and never reads one as a
 * choice.
 *
 * On the device under one entry PER SHOP, holding either the list or an
 * explicit "all": two shops on one machine keep separate Tuesdays, and "All
 * games" chosen today is not overridden by yesterday's Magic tomorrow — which
 * is what a bare absence of an entry could not promise. The same kind of
 * memory as `cbm.locale` and `cbm.settings`: a working context, which is a
 * property of the day and the screen more than of the shop, which is why it is
 * on the device and not on the account.
 *
 * RESOLUTION ON OPENING: the address if it names a game; else this device's
 * memory for this shop; else All games. The address wins because the
 * recipient of a link is not its sender — browse's rule, copied. A change
 * writes both; an arrival writes neither.
 */
const memoryKey = (shopId) => `cbm.shopScope.${shopId}`;

/**
 * What this device remembers for this shop: the list it chose, `[]` for an
 * explicit "All games", or null for nothing — no entry, or one that cannot be
 * read — which is the arrival effect's cue to fall through to All games. The
 * two are told apart on purpose: "All games" chosen is a choice, and an
 * answer that read the same for a choice and for an absence would make the
 * explicit flag `rememberScope` writes a flag nothing ever read. A game the
 * product does not know is dropped rather than sent: the stock endpoint
 * refuses it, and a refused request is an empty table with an error nobody
 * can act on.
 */
function rememberedScope(shopId) {
  try {
    const raw = window.localStorage.getItem(memoryKey(shopId));
    const stored = raw ? JSON.parse(raw) : null;
    if (stored && Array.isArray(stored.games)) return stored.games.filter((g) => KNOWN_GAMES.has(g));
    return stored && stored.all === true ? [] : null;
  } catch (e) {
    return null;
  }
}

function rememberScope(shopId, games) {
  try {
    window.localStorage.setItem(memoryKey(shopId), JSON.stringify(games.length ? { games } : { all: true }));
  } catch (e) {
    // A browser refusing storage still has the address.
  }
}

/** The scope as the address carries it: All games is the ABSENCE of the key (see above), so an empty scope is no key at all. */
const scopeParam = (games) => (games.length ? games : undefined);

/**
 * WHICH GAMES THIS SCREEN IS ABOUT, carried across the sections.
 *
 * A shop that sells four games is four shops' worth of stock in one list, and
 * the design puts this bar above everything for that reason.
 *
 * IT NARROWS ON THE SERVER, not in the browser. A chip that filtered the page
 * already fetched would say "Magic" over the Magic cards out of the newest
 * fifty — a lie that gets worse the bigger the shop, which is the one place
 * this feature is for. The counts beside each game come back unfiltered, so
 * narrowing to one does not empty the others.
 *
 * ALWAYS DRAWN once the shop is acting as itself — one game in stock, four,
 * or none yet, when it reads "All games" alone. It used to hide itself under
 * two games, and that rule hid its own bug: the tally the stock endpoint
 * returns is keyed by game ID, and this looked it up by the game OBJECT
 * (`counts[{ id, label }]` is `counts["[object Object]"]`), found nothing for
 * every game, and so never drew for anybody. The regression test is the
 * back-office story about a one-game shop seeing the bar.
 */
function ScopeBar({ scope, onScope }) {
  const { myCardsByGame, locale, t } = useApp();
  const counts = myCardsByGame || {};
  const stocked = GAMES.map((g) => g.id).filter((id) => counts[id]);
  /*
   * EVERY STOCKED GAME LIT READS AS ALL GAMES — decided by WHICH games are
   * lit, never by how many. A link can name a game the shop does not stock
   * (`?game=lorcana`): it has no chip, and counting it would make Lorcana
   * plus Magic "all" of a Pokémon-and-Magic shop. When the collapse does
   * happen, a stray game like that goes with the key, which is the one way
   * to say everything.
   */
  const toggle = (g) => {
    const next = scope.includes(g) ? scope.filter((x) => x !== g) : [...scope, g];
    onScope(stocked.every((id) => next.includes(id)) ? [] : next);
  };
  return (
    <div className="sh-scope" role="group" aria-label={t("Games shown")}>
      <span className="cbm-label">{t("Showing")}</span>
      <div className="sh-scope__tags">
        <Tag selected={!scope.length} onClick={() => onScope([])}>{t("All games")}</Tag>
        {stocked.map((g) => (
          <Tag key={g} selected={scope.includes(g)} onClick={() => toggle(g)}>
            {gameLabel(g)} <span className="sh-scope__n">{shortCount(counts[g], locale)}</span>
          </Tag>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- today */

/** How long a card has been on the shelf, in the only unit a shopkeeper uses. */
function daysOnShelf(createdAt) {
  if (!createdAt) return null;
  const then = new Date(createdAt).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

/**
 * THE CONNECTION WARNING (ADR 0011), at the top of every section of the back
 * office while it is raised: a shop's CardTrader connection has stopped
 * working, whichever half — the sweep or write-back — met the problem. The
 * server stores only a code; the sentences, and what to do, are made here.
 *
 * Two messages, because they ask for opposite things: a refused token needs
 * the shop to connect a fresh one, and gets the button; an outage needs
 * nothing and says so — regenerating a token that was fine is the worst advice
 * there is. Nothing to dismiss: the next success clears it by itself.
 */
function ConnectionWarning({ warning, go }) {
  const { t } = useApp();
  if (!warning) return null;
  const refused = warning.reason === 'token_refused';
  /* Day before month and a 24-hour clock in both languages: the brand's rule, through the app's one formatter. */
  const when = formatWhen(warning.since);
  return (
    <div role="alert" data-cbm="connection-warning"
         style={{
           display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', flexWrap: 'wrap',
           marginBottom: 'var(--space-6)', padding: 'var(--space-4) var(--space-5)',
           background: 'var(--orange-100)', borderLeft: '3px solid var(--orange-500)', borderRadius: 'var(--radius-sm)',
         }}>
      <span style={{ color: 'var(--orange-700)', paddingTop: 2 }}><Icon name="alert-triangle" size={18} /></span>
      <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        <strong style={{ font: 'var(--type-ui)', color: 'var(--text-strong)' }}>
          {refused
            ? t("CardTrader no longer accepts your shop's token.")
            : t("CardTrader has not answered for over an hour.")}
        </strong>
        <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-body)' }}>
          {refused
            ? t("Until you connect a fresh token, your stock here does not follow CardTrader's, and copies you promise in trades here are not taken off there. Generate a new one in your CardTrader profile (settings → API) and paste it on your store page.")
            : t("While it lasts, your stock here is not updated and anything to take off CardTrader waits. There is nothing to do: we keep trying, and this goes by itself as soon as CardTrader answers.")}
        </span>
        <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>{t("Failing since {when}", { when })}</span>
      </div>
      {refused ? (
        <Button size="sm" variant="outline" onClick={() => go('shop')}>{t("Connect it again")}</Button>
      ) : null}
    </div>
  );
}

function ShopToday({ shop, demand, demandWaiting, go, cardtrader }) {
  const { trades, tradesState, myCardsTotal, myCardsState, listedPendingTrades: pendingTrades, locale, t } = useApp();
  const waiting = trades.filter(waitsOnYou);
  const nf = new Intl.NumberFormat(locale);
  /*
   * WAITING WITHOUT MOVING. A chip on the scope bar re-asks the stock, the
   * demand AND the trades — "Waiting on you" is counted off the trades list
   * — and the figures keep the numbers they had, dimmed, until the narrowed
   * answers land: the Stock table's rule, applied to the numbers above it. A
   * number that collapsed to a dash and grew back would move the row twice
   * for one tap. All three loads are in the dim, so it is never lifted while
   * one of the three numbers is still the old one.
   */
  const figuresWaiting = demandWaiting || myCardsState === 'loading' || tradesState === 'loading';

  return (
    <>
      <SectionHead title={t("Today")} sub={shop.name} />

      {/* `sh-today__figures` styles nothing: it is the hook the tests measure this box by while it waits. */}
      <div className="sh-today__figures sh-waits" aria-busy={figuresWaiting}>
        <StatRow items={[
          { label: t("Waiting on you"), value: pendingTrades },
          { label: t("Listings live"), value: myCardsTotal == null ? '—' : shortCount(myCardsTotal, locale) },
          /*
           * TRADERS, NOT WANTS. One person with four of your cards on their list
           * is one person who might walk in; counting the wants would say four
           * and send a shopkeeper looking for four customers.
           */
          { label: t("People looking for your cards"), value: demand ? nf.format(demand.tradersWanting) : '—' },
          /*
           * WRITES CARDTRADER REFUSED (ADR 0011): copies a trade here should
           * have taken off, or put back, that did not land. Only for a shop
           * that connected CardTrader — to anybody else the number means
           * nothing — and known on the first frame, so it never pops in.
           */
          ...(cardtrader ? [{ label: t("CardTrader writes that failed"), value: nf.format(cardtrader.failedWrites) }] : []),
        ]} />
      </div>

      <div className="sh-grid2" style={{ marginTop: 'var(--space-8)' }}>
        <Card padding="var(--space-5)">
          <div className="cbm-dash__head">
            <h2 className="cbm-h2" style={{ fontSize: 'var(--text-lg)', margin: 0 }}>{t("Waiting on you")}</h2>
            <button type="button" className="cbm-textlink" onClick={() => go.to('requests')}>{t("All requests")}</button>
          </div>
          {tradesState === 'loading' && !trades.length
            ? <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>{t("Loading…")}</span>
            : waiting.length
              ? waiting.slice(0, 4).map((x) => (
                /* Opens THIS trade in Requests, not the section it is somewhere in. */
                <button key={x.id} type="button" className="cbm-dash__row" onClick={() => go.to('requests', { id: x.id })}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="cbm-dash__who">{x.withWhom}</div>
                    <div className="cbm-dash__meta">
                      {x.can && x.can.accept ? t("Wants to trade") : t("Waiting to be completed")}
                    </div>
                  </div>
                  <Icon name="chevron-right" size={16} />
                </button>
              ))
              : <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>
                  {t("Nothing is waiting on you. New requests show up here first.")}
                </span>}
        </Card>

        <Card padding="var(--space-5)">
          <div className="cbm-dash__head">
            <h2 className="cbm-h2" style={{ fontSize: 'var(--text-lg)', margin: 0 }}>{t("Most looked for, in your stock")}</h2>
            <button type="button" className="cbm-textlink" onClick={() => go.to('stock')}>{t("Stock")}</button>
          </div>
          {/* `sh-today__wanted` likewise: a hook for the tests, not a style. */}
          <div className="sh-today__wanted sh-waits" aria-busy={demandWaiting}>
            {demand && demand.wanted.length
              ? demand.wanted.map((w) => (
                <div key={w.listingId} className="sh-line">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="cbm-dash__who">{w.title}</div>
                    <div className="cbm-dash__meta">{gameLabel(w.game)}{setNameFor(w, locale) ? ` · ${setNameFor(w, locale)}` : ''}</div>
                  </div>
                  <span className="cbm-dash__n">{nf.format(w.traders)}</span>
                  <span className="cbm-dash__meta">{w.traders === 1 ? t("person") : t("people")}</span>
                </div>
              ))
              : <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>
                  {t("Nobody nearby is looking for anything you have listed yet.")}
                </span>}
          </div>
        </Card>
      </div>

      {/*
        NOT MOVING: a month on the shelf and nobody has asked for it. The design
        counted views; the owner replaced that with this, and it is the sharper
        question — a card nobody has on a list is one to reprice, whoever
        happened to scroll past it.
      */}
      {/*
        THE "NOBODY LOOKING" STRIP WENT WITH THE TAB IT DROVE TO.
        Its button opened that tab, so removing one and keeping the other would
        leave a card offering to show a shop something it cannot be shown. The
        reason is the same either way: with two wants on the platform, "a month
        on the shelf and nobody has it on a list" is true of nearly every card
        and tells a shop nothing about its own stock.
      */}
      {shop.status === 'waiting' ? (
        <Card tone="sunken" padding="var(--space-4)"
              style={{ marginTop: 'var(--space-5)', display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
          <Icon name="clock" size={18} style={{ flexShrink: 0 }} />
          <div>
            <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>
              {t("Waiting to be checked")}
            </div>
            {/*
              THE SHOP CAN ALREADY WORK, which is the one thing worth saying
              here. Verification buys a badge and an address on the map; the
              stock, the trades and the inbox have been the shop's since the
              moment it was created, and a shopkeeper who thinks they have to
              wait two days to start is one who comes back in two days.
            */}
            <div style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 4 }}>
              {t("You can list stock and answer requests now. The badge and the map pin come with the check.")}
            </div>
          </div>
        </Card>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------- stock */

/**
 * The price of one card, edited where it is read.
 *
 * COMMITTED ON LEAVING THE FIELD, which is the design's own behaviour and the
 * right one for a table: a shopkeeper walking down a column of prices should
 * not have to press anything. Escape puts it back, because an edit nobody meant
 * has to be undoable without knowing what the old number was.
 */
function PriceCell({ listing, onCommit, disabled, readOnly, title }) {
  const { t } = useApp();
  const asText = (cents) => (cents == null ? '' : (cents / 100).toFixed(2));
  const [text, setText] = React.useState(asText(listing.priceCents));
  React.useEffect(() => { setText(asText(listing.priceCents)); }, [listing.priceCents]);

  const commit = () => {
    const cents = Math.round(parseFloat(text.replace(',', '.')) * 100);
    if (Number.isFinite(cents) && cents > 0 && cents !== listing.priceCents) onCommit(cents);
    else setText(asText(listing.priceCents));
  };
  /*
   * A SYNCED CARD'S PRICE IS READ, NOT EDITED (ticket 11): the same box, so the
   * column stays one width, holding the number as text rather than a field —
   * a disabled input still looks like something a shop could unlock. Formatted
   * for the locale, since it is now read rather than typed.
   */
  if (readOnly) {
    return (
      /* `flex: 1` because there is no input to size it: an input's own
         width is what stretches the editable box across its cell. */
      <div className="sh-price" title={title} data-cbm="price-read-only" style={{ flex: '1 1 0', minWidth: 0 }}>
        <span style={{ color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {api.formatCents(listing.priceCents)}
        </span>
      </div>
    );
  }
  return (
    <div className="sh-price" title={title}>
      <span>€</span>
      <input
        inputMode="decimal" value={text} disabled={disabled}
        aria-label={t("Price of {card}", { card: listing.title })}
        onChange={(e) => setText(e.target.value.replace(/[^0-9.,]/g, ''))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') { setText(asText(listing.priceCents)); e.currentTarget.blur(); }
        }}
      />
    </div>
  );
}

/** Changing many prices at once: a percentage, or one number for all of them. */
/**
 * FOLLOWING CARDMARKET, EXPLAINED BEFORE IT HAPPENS.
 *
 * Reported: the button "should show a dialog explaining the feature before
 * applying", and then "also the usual form to define how to follow the price".
 *
 * It used to apply on the click. That is the wrong shape for this particular
 * action twice over: it hands a shop's prices to a nightly job — money, moved
 * while nobody is looking — and it silently chose the default basis on their
 * behalf. A button that changes three hundred prices should say so first.
 *
 * THE USUAL FORM, NOT A SECOND ONE. `MarketFollowFields` is the control the
 * posting form and the edit dialog already use, and its own note says why there
 * is one copy: two would agree on the common case and disagree on the one
 * nobody tried. A shop asking "which number, adjusted how" is asking the same
 * question as everybody else.
 *
 * NO PRICES PASSED IN. That control draws a preview when it knows the card's
 * low and average, and here it is about to apply to a whole selection with as
 * many prices as there are rows. Showing one card's numbers would be showing
 * the wrong card's; the sentence below says what will happen instead.
 */
function BulkFollowMarketDialog({ count, onClose, onApply }) {
  const { t } = useApp();
  const [market, setMarket] = React.useState({ followsMarket: true, adjust: null, basis: 'low', valid: true });
  return (
    <ModalHost>
      <Dialog
        open width={560} closeLabel={t("Close")} onClose={onClose}
        data-cbm="bulk-follow"
        title={t("Follow the market on {count} listings", { count })}
        description={t("Every night we read the price on Cardmarket and set yours from it. A card we cannot match keeps the price you typed, and a trade already proposed keeps the price it was agreed at.")}
        footer={<>
          <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
          <Button disabled={!market.valid} onClick={() => onApply(market)}>
            {t("Start following")}
          </Button>
        </>}
      >
        <div className="cbm-stack" style={{ gap: 'var(--space-5)' }}>
          <MarketFollowFields
            initial={{ followsMarket: true, basis: 'low', adjust: null }}
            onChange={setMarket}
            note={t("You can stop at any time. The price stays where it last landed and is yours to edit again.")}
          />
        </div>
      </Dialog>
    </ModalHost>
  );
}

/**
 * WHAT THIS ROW'S RULE ACTUALLY IS, reached from the mark that says it has one.
 *
 * Asked for: "We should be able to also check what's the configuration for
 * following a cardmarket price per row. For example, clicking their icon next
 * to the price."
 *
 * The mark told a seller THAT a price is tracking Cardmarket and nothing about
 * HOW — which basis, which adjustment — and the only way to find out was the
 * full editor. A badge that states a fact and cannot be asked about it is half a
 * sentence.
 *
 * THE SAME CONTROL THE OTHER TWO USE, not a third description of the same
 * settings. `MarketFollowFields` is what the posting form, the edit dialog and
 * the bulk dialog all draw; three copies of a rule's wording is how three copies
 * disagree. Here it is handed the card's REAL low and average, so it shows what
 * the rule currently produces rather than describing it — "check the
 * configuration" is answered best by the number it comes out at.
 *
 * IT ALSO CHANGES IT. Checking and changing are the same screen for the same
 * reason the bulk dialog asks before applying: somebody who opens this to see
 * the rule is, most of the time, somebody who wants it to be different.
 */
function MarketRuleDialog({ listing, onClose, onApply }) {
  const { t } = useApp();
  const pricing = listing.pricing || {};
  const [market, setMarket] = React.useState({
    followsMarket: true, adjust: pricing.adjust || null,
    basis: pricing.basis || 'low', valid: true,
  });
  return (
    <ModalHost>
      <Dialog
        open width={520} closeLabel={t("Close")} onClose={onClose}
        data-cbm="market-rule"
        title={listing.title}
        description={t("How this price follows Cardmarket. It is read and applied every night.")}
        footer={<>
          <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
          <Button disabled={!market.valid} onClick={() => onApply(market)}>{t("Save")}</Button>
        </>}
      >
        <MarketFollowFields
          lowCents={pricing.lowCents}
          avgCents={pricing.avgCents}
          initial={{ followsMarket: true, basis: pricing.basis || 'low', adjust: pricing.adjust || null }}
          onChange={setMarket}
          note={t("Switch it off to go back to typing the price yourself. It stays where it last landed.")}
        />
      </Dialog>
    </ModalHost>
  );
}

function BulkPriceDialog({ count, onClose, onApply }) {
  const { t } = useApp();
  const [mode, setMode] = React.useState('pct');
  const [pct, setPct] = React.useState(-10);
  const [flat, setFlat] = React.useState('');
  const flatCents = Math.round(parseFloat((flat || '').replace(',', '.')) * 100);
  const valid = mode === 'pct' ? pct !== 0 : Number.isFinite(flatCents) && flatCents > 0;
  return (
    <ModalHost>
      <Dialog
        open width={500} closeLabel={t("Close")} onClose={onClose}
        data-cbm="bulk-price"
        title={t("Change {count} prices", { count })}
        description={t("Prices never go below one cent. A card that follows Cardmarket keeps its own price.")}
        footer={<>
          <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
          <Button disabled={!valid} onClick={() => onApply(
            mode === 'pct'
              ? { unit: 'basisPoints', value: Math.round(pct * 100) }
              : { unit: 'cents', value: flatCents },
          )}>{t("Change them")}</Button>
        </>}
      >
        <div className="cbm-stack" style={{ gap: 'var(--space-5)' }}>
          <Radio value={mode} onChange={setMode} options={[
            { value: 'pct', label: t("Adjust by a percentage") },
            { value: 'fixed', label: t("Set one price for all") },
          ]} />
          {mode === 'pct' ? (
            <div className="cbm-stack" style={{ gap: 10 }}>
              <div className="cbm-wrap">
                {[-20, -10, -5, 5, 10].map((n) => (
                  <Tag key={n} selected={pct === n} onClick={() => setPct(n)}>
                    {(n > 0 ? '+' : '−') + Math.abs(n) + '%'}
                  </Tag>
                ))}
              </div>
              <Input
                label={t("Or type a percentage")} suffix="%" inputMode="numeric" value={String(pct)}
                hint={t("Negative to lower, positive to raise.")}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9-]/g, '');
                  setPct(raw === '' || raw === '-' ? 0 : Math.max(-99, Math.min(1000, parseInt(raw, 10))));
                }} />
            </div>
          ) : (
            <Input label={t("New price")} suffix="€" inputMode="decimal" placeholder="2,00"
                   value={flat} onChange={(e) => setFlat(e.target.value.replace(/[^0-9.,]/g, ''))} />
          )}
        </div>
      </Dialog>
    </ModalHost>
  );
}

/*
 * HOW THE STOCK TABLE MAY BE ORDERED, as the server names each ordering — the
 * owner, 2026-10-04: "sorting by name, price or condition could be helpful."
 * The key travels on the request and is what the cursor pages on, so it is
 * never a translated sentence; the label is the English sentence that IS the
 * translation key. Browse's own labels where browse already has the ordering.
 */
/** The Stock drawer's name on its history entry (`pushOverlayEntry`). */
const SORT_SHEET = 'stock-sort';
const STOCK_SORTS = [
  { key: 'newest', label: 'Newest' },
  { key: 'oldest', label: 'Oldest first' },
  { key: 'price_asc', label: 'Price: low to high' },
  { key: 'price_desc', label: 'Price: high to low' },
  { key: 'name', label: 'Name: A to Z' },
  { key: 'name_desc', label: 'Name: Z to A' },
  { key: 'condition', label: 'Condition: best first' },
  { key: 'condition_desc', label: 'Condition: worst first' },
];
/*
 * Which way each order runs, for the column header that shows it (`aria-sort`
 * and its arrow). Every column has both (the owner, 2026-10-22: "the condition
 * one does not have asc and desc. Only one direction"): best condition first
 * reads as the column running downwards, from mint, and newest as the dates
 * running downwards, so each one's reverse is the other way.
 */
const SORT_DIRECTION = {
  newest: 'descending', oldest: 'ascending', price_asc: 'ascending', price_desc: 'descending',
  name: 'ascending', name_desc: 'descending', condition: 'descending', condition_desc: 'ascending',
};

/*
 * The Stock table's view as the link states it, with anything it does not
 * know read as the default — a hand-edited or stale link is the plain table,
 * never an error. Only under `sec=stock`: the same keys mean nothing, or
 * something else, anywhere else in the back office.
 */
function stockFromLink(route) {
  const here = route.name === 'dashboard' && route.sec === 'stock';
  return {
    q: here && typeof route.q === 'string' ? route.q : '',
    // A picked card's English name, which the search reads where the box shows another language's.
    card: here && typeof route.card === 'string' ? route.card : '',
    synced: here && route.synced === '1',
    sort: here && STOCK_SORTS.some((s) => s.key === route.sort) ? route.sort : 'newest',
  };
}

function ShopStock({ route, go, scope = [], cardtrader = null }) {
  const {
    myCards, myCardsState, myCardsTotal, loadMine, cursors, loadMore, loadingMore,
    editListing, withdrawListings, bulkPrice, setFollowingInBulk, locale, notify, t,
  } = useApp();
  /*
   * THE SEARCH, THE CHIP AND THE ORDER LIVE IN THE LINK (owner, 2026-10-22), so
   * a reload or a shared link opens the same table. Read once, here, for this
   * screen's controls; the shell reads the same keys for the first request of
   * the stock (`stockFromLink`), so arriving costs one request, not two.
   * Written back REPLACED, as the game scope is: Back leaves a section, it does
   * not step through keystrokes. Leaving the section through the rail drops
   * them, as it always did.
   */
  const linked = stockFromLink(route);
  const [term, setTerm] = React.useState(linked.q);
  /*
   * A PICKED CARD'S ENGLISH NAME, which is what the stock search reads, beside
   * the name the box shows ("Elfos de Llanowar"). Browse's `pickCard`, the same
   * rule and the same `card` key in the link; typing drops it.
   */
  const [cardName, setCardName] = React.useState(linked.card);
  const searched = cardName || term;
  const link = (patch) => {
    const next = { q: term, card: cardName, synced: onlySynced, sort, ...patch };
    go.to('stock', {
      q: next.q || undefined,
      card: next.card || undefined,
      synced: next.synced ? '1' : undefined,
      sort: next.sort !== 'newest' ? next.sort : undefined,
    }, { replace: true, keepScroll: true });
  };
  const search = (value) => { setTerm(value); setCardName(''); link({ q: value, card: '' }); };
  const pickCard = (item) => {
    const shownName = suggestionNameFor(item);
    const english = shownName !== item.name ? item.name : '';
    setTerm(shownName);
    setCardName(english);
    link({ q: shownName, card: english });
  };
  /*
   * ONLY THE SYNCED ROWS — the owner, 2026-10-20, of the CardTrader badge's
   * filter: "Cool. Let's add it." Asked of the server (`synced=1`), for the
   * reason the search is: the shelf, not the page already fetched.
   *
   * It narrows the table the way a typed name does, so it takes the head
   * box's "everything" off and the box then ticks rows by id. The server's
   * "all" follows a scope of games and nothing else, and every bulk action
   * that would send it — price, Follow — is one a synced card refuses anyway.
   */
  const [onlySynced, setOnlySynced] = React.useState(linked.synced);
  const syncedRef = React.useRef(linked.synced);
  const showSynced = (on) => {
    /* Every tick comes off too: a row ticked and then hidden by the chip
       would still be taken down by a button that names it nowhere. */
    setAll(false);
    setPicked(new Set());
    setOnlySynced(on);
    syncedRef.current = on;
    loadMine({ synced: on });
    link({ synced: on });
  };
  /*
   * The narrowing is the store's and outlives this screen, so leaving with it
   * on puts the whole shelf back. Through a ref: the loader of the first
   * render would still know the account of the first render.
   */
  const loadMineRef = React.useRef(loadMine);
  loadMineRef.current = loadMine;
  /* The search too, since 2026-10-22: the box comes back empty, so the shelf must. */
  const termRef = React.useRef(term);
  termRef.current = term;
  React.useEffect(() => () => {
    if (syncedRef.current || sortRef.current !== 'newest' || termRef.current) {
      loadMineRef.current({ synced: false, sort: 'newest', q: '' });
    }
  }, []);
  /*
   * THE ORDER, ASKED OF THE SERVER like the chip and the search: the table
   * pages on it, and the count and every narrowing still apply under it. In
   * the link with them (see `linked`), and put back to newest on leaving,
   * through the same unmount as the chip.
   *
   * The ticks come off, as the chip's do: a row ticked on the page before may
   * not be on the one after. "Everything in scope" stays, because reordering
   * the shelf does not change what is in it.
   */
  const [sort, setSort] = React.useState(linked.sort);
  const sortRef = React.useRef(linked.sort);
  const sortBy = (key) => {
    /* The order already on is not asked for again: picking it twice in the
       drawer is nothing, not a reload. */
    if (key === sortRef.current) return;
    setPicked(new Set());
    setSort(key);
    sortRef.current = key;
    loadMine({ sort: key });
    /*
     * Picked in the drawer, the order is written into the link once the
     * drawer's history entry is off (see `closeSorting`): written now, it
     * would land on THAT entry, and popping it would take the order with it.
     */
    if (onOverlayEntry(SORT_SHEET)) linkAfterSheet.current = { sort: key };
    else link({ sort: key });
  };
  /*
   * A COLUMN HEADER THAT SORTS (design D, the owner's pick): the first click
   * asks for the column's first order, a second click on the column already
   * sorting asks for its reverse — A to Z and back, cheapest and dearest,
   * best condition and worst, newest and oldest.
   */
  const sortHead = (label, keys, className) => {
    const on = keys.includes(sort);
    const direction = on ? SORT_DIRECTION[sort] : 'none';
    const next = on ? keys[(keys.indexOf(sort) + 1) % keys.length] : keys[0];
    return (
      <span role="columnheader" aria-sort={direction} className={className}>
        <button type="button" className="cbm-label sh-sorthead" data-on={on ? '1' : undefined}
                onClick={() => sortBy(next)}>
          {t(label)}
          <Icon name={on ? (direction === 'ascending' ? 'arrow-up' : 'arrow-down') : 'chevrons-up-down'}
                size={12} strokeWidth={on ? 2.5 : 2}
                color={on ? 'var(--text-link)' : 'var(--text-faint)'} />
        </button>
      </span>
    );
  };
  /*
   * The phone's drawer of orders (design D): false, 'opening', 'open' or
   * 'closing'.
   *
   * 'OPENING' lasts no time at all: the drawer mounts in its closed values
   * (app.css), the layout effect below makes the browser read them, and turns
   * it 'open' before anything is painted — which is what gives the transition
   * up something to start from.
   *
   * 'CLOSING' IS STILL MOUNTED, so the sheet can slide down and the scrim fade
   * out instead of vanishing (the owner, 2026-10-22: "Would be nice to animate
   * the opening and closing of the drawer on mobile"). It ends when the exit
   * transitions finish — asked of the transitions themselves, not a timer with
   * the duration copied into it, so under reduced motion (no slide, the
   * blanket rule's .01ms) it ends at once, and with no animation at all it
   * ends immediately. Because the ModalHost stays mounted through it, the
   * scroll lock and the Android pull-to-refresh stand-down are released only
   * once the sheet is gone. Then focus goes back to the Ordenar button.
   *
   * BACK CLOSES IT (the owner, 2026-10-22: "I believe that back should close
   * the drawer. It would be a pretty expected interaction for mobile").
   * Opening pushes an entry of its own at the same address
   * (`pushOverlayEntry`), and leaving that entry is what closes the drawer:
   * Back, the Android app's Back (which goes through `window.cbmBack` and
   * `history.back()`), or `closeSorting` taking the entry off for every other
   * way out — a pick, Close, the scrim, Escape — so none of them leaves a Back
   * that does nothing behind.
   *
   * A pick applies its order at once — the request does not wait for the
   * animation or the history — but writes it into the link only after the
   * entry is off (`linkAfterSheet`), onto the page's own entry.
   *
   * Arriving ON the drawer's entry: by Forward, the drawer opens again; by a
   * reload, the mark is wiped and the drawer stays shut — the entries behind
   * a reload belong to the page that was, and Back from a reopened drawer
   * would load it again.
   */
  const [sorting, setSorting] = React.useState(false);
  const sortHost = React.useRef(null);
  const sortOpener = React.useRef(null);
  const linkAfterSheet = React.useRef(null);
  const linkRef = React.useRef(link);
  linkRef.current = link;
  const openSorting = () => { pushOverlayEntry(SORT_SHEET); setSorting('opening'); };
  const closeSorting = () => {
    setSorting((s) => (s === 'opening' || s === 'open' ? 'closing' : s));
    if (onOverlayEntry(SORT_SHEET)) window.history.back();
  };
  React.useEffect(() => {
    if (onOverlayEntry(SORT_SHEET)) window.history.replaceState({}, '', window.location.href);
    const onPop = () => {
      if (onOverlayEntry(SORT_SHEET)) {
        /* Forward, into the drawer: open, where the button that opens it is drawn. */
        const opener = sortOpener.current;
        if (opener && opener.offsetParent !== null) setSorting((s) => s || 'opening');
        return;
      }
      setSorting((s) => (s === 'opening' || s === 'open' ? 'closing' : s));
      const pending = linkAfterSheet.current;
      linkAfterSheet.current = null;
      if (pending) linkRef.current(pending);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  React.useLayoutEffect(() => {
    if (sorting !== 'opening') return;
    /* Read, so the closed values are the ones the browser has before `is-open` lands. */
    if (sortHost.current) void getComputedStyle(sortHost.current.querySelector('.sh-sortsheet') || sortHost.current).transform;
    setSorting('open');
  }, [sorting]);
  React.useEffect(() => {
    if (sorting !== 'open') return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeSorting(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sorting]);
  React.useEffect(() => {
    if (sorting !== 'closing') return undefined;
    let gone = false;
    const done = () => {
      if (gone) return;
      gone = true;
      setSorting(false);
      const opener = sortOpener.current && sortOpener.current.querySelector('button');
      if (opener) opener.focus({ preventScroll: true });
    };
    const host = sortHost.current;
    /* An endless animation under the sheet (a spinner, one day) never finishes: not waited on. */
    const running = (host && typeof host.getAnimations === 'function' ? host.getAnimations({ subtree: true }) : [])
      .filter((a) => !a.effect || a.effect.getComputedTiming().iterations !== Infinity);
    if (running.length === 0) done();
    /* `finished` rejects when an animation is cancelled; that is an end too. */
    else Promise.all(running.map((a) => a.finished)).then(done, done);
    return () => { gone = true; };
  }, [sorting]);
  const [picked, setPicked] = React.useState(() => new Set());
  /*
   * EVERYTHING IN SCOPE, which is a different thing from every row ticked by
   * hand. The head box sets it; the bulk actions then send the server the
   * `all` flag with the scope's games beside it, and the server resolves "all
   * of mine in these games" as it writes — never a list this screen assembles,
   * which would grow with the stock and be stale the moment another tab posted
   * a card. Repricing the Magic stock under a Magic scope leaves the Pokémon
   * stock alone; under All games it is the whole counter, as it always was.
   */
  const [all, setAll] = React.useState(false);
  const [dialog, setDialog] = React.useState(null);
  /*
   * The row being edited, which is a different thing from `dialog` above: that
   * one names a BULK action over the selection, this one is a single listing.
   */
  const [editing, setEditing] = React.useState(null);
  /* The row whose Cardmarket rule is being read, which is its own thing again:
     a mark somebody pressed, not the pencil and not a bulk action. */
  const [rule, setRule] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  /*
   * THE SEARCH GOES TO THE SERVER, after a pause. A shop with four thousand
   * cards cannot find one by scrolling, and filtering the page already fetched
   * would search the newest fifty rather than the shelf.
   *
   * Skipped on the first render: the shell has already asked for the shelf, and
   * a second identical request on arrival is one nobody asked for.
   */
  const typed = React.useRef(false);
  React.useEffect(() => {
    if (!typed.current) { typed.current = true; return undefined; }
    /*
     * A name typed narrows the table past what "everything in scope" can
     * mean; the tick comes off with it. A chip tap does not clear it: the
     * server's "all" follows a scope and not a search, so "everything" simply
     * re-points at the new scope, and the bar's count says the new number.
     */
    setAll(false);
    const id = setTimeout(() => loadMine({ q: searched }), 300);
    return () => clearTimeout(id);
  }, [searched]);

  const rows = myCards;
  const shown = rows.map((c) => c.id);
  const allOn = all || (shown.length > 0 && shown.every((id) => picked.has(id)));
  const someOn = !allOn && shown.some((id) => picked.has(id));
  const clear = () => { setAll(false); setPicked(new Set()); };
  /*
   * THE HEAD BOX MEANS EVERYTHING IN SCOPE — the scope bar says "Showing",
   * and this ticks what it shows, past the page on screen. Except under a
   * typed name or the Synced chip: both narrow the table in a way the server's
   * "all" cannot follow, so the box then ticks the rows shown, by id.
   */
  const toggleAll = () => {
    if (allOn) clear();
    else if (!term && !onlySynced) { setAll(true); setPicked(new Set()); }
    else setPicked(new Set(shown));
  };
  const toggle = (id, on) => {
    /* Unticking one row under "everything" leaves it: the rest on screen stay ticked, by id. */
    if (all) { setAll(false); setPicked(new Set(shown.filter((x) => x !== id))); return; }
    setPicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };
  const chosen = all ? shown : [...picked];
  const selected = all ? (myCardsTotal ?? shown.length) : chosen.length;
  /*
   * WHICH LISTINGS A BULK ACTION NAMES. A scope of no games is All games and
   * travels as the absence of the key, as the stock request sends it.
   */
  const selection = () => (all
    ? { all: true, ...(scope.length ? { game: scope } : {}) }
    : { listingIds: chosen });
  /*
   * Taking down goes by id — the withdraw route has no "all" — so under
   * "everything" it is offered only while every row in scope is on screen,
   * where the ids ARE all of it. Past that page the button is greyed rather
   * than taking down fifty while the bar says four thousand.
   */
  const canTakeDown = selected > 0 && (!all || selected <= shown.length);
  /* Said, when it is greyed for that reason: a greyed button that says nothing reads as broken. */
  const takeDownWhy = all && selected > 0 && !canTakeDown
    ? t("Taking down works on the rows on screen. Narrow the list, or take them down a page at a time.")
    : null;
  /*
   * UNDER "SYNCED", NO PRICE ACTIONS: every row on screen takes its price from
   * CardTrader, so the server would skip all of them. Stopping following
   * stays — it is how a card that followed before the rule is put straight —
   * and so does taking them down.
   *
   * AND WHEREVER EVERY TICKED ROW IS SYNCED, chip or not. The owner, 2026-10-21,
   * ticking synced rows on the whole shelf: "the buttons you mention are not
   * disabled and can actually be used. But they do not change the state after
   * all" — the server skipped every one, and a button that does nothing reads
   * as broken. A mix stays offered: the hand-posted rows change, and the
   * result says how many were left to CardTrader.
   */
  const syncedShown = new Set(rows.filter((c) => c.pricing && c.pricing.managedOnCardtrader).map((c) => c.id));
  const pricedElsewhere = onlySynced
    || (!all && chosen.length > 0 && chosen.every((id) => syncedShown.has(id)));
  const pricedElsewhereWhy = pricedElsewhere && selected > 0
    ? t("Synced cards take their price from CardTrader, so it is changed there.")
    : null;

  const takeDown = async () => {
    setBusy(true);
    try {
      await withdrawListings(chosen);
      clear();
      setDialog(null);
    } catch (e) { /* the store has already said so */ } finally { setBusy(false); }
  };

  const applyPrices = async (change) => {
    setBusy(true);
    try {
      await bulkPrice({ ...selection(), change });
      clear();
      setDialog(null);
    } catch (e) { /* said already */ } finally { setBusy(false); }
  };

  return (
    <>
      <SectionHead
        title={t("Stock")}
        sub={myCardsTotal == null
          ? undefined
          : t("{n} live. Prices save when you leave the box.", { n: shortCount(myCardsTotal, locale) })}
        right={<div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <Button variant="outline" iconLeft="upload" onClick={() => go.open('import', { kind: IMPORT_SOURCES.stock })}>
            {t("Import inventory")}
          </Button>
          <Button iconLeft="plus" onClick={() => go.open('post')}>{t("Post a card")}</Button>
        </div>} />

      {/*
        NO TAB BAR, BECAUSE ONE TAB IS NOT A CHOICE.

        "Nobody looking" was the only other one and it has gone — it asked for
        listings a month old that no WANT matches, which with two wants on the
        whole platform flagged 48 of a shop's 49 cards and told them something
        about our demand side while addressing the sentence to their stock.

        Leaving "All" behind on its own would draw a control that selects the
        thing already selected. The filters below — game, search — are what
        actually narrows this table, and they are still here.

        Worth bringing both back when wants are in the thousands; the server
        filter was left in place for that day. See docs/TODO.md.
      */}
      {/*
        THE OWNER'S PICK OF THE FOUR LAYOUTS ("Elegida", design D, 2026-10-22):
        "the sorting integrated into the table columns in desktop and the
        'chip' with the drawer, but instead of a chip I'd use a button. I think
        it's important to visually distinguish between a chip (something that
        can be toggled) and a button. Then I'd use the filters from B, but
        instead of showing them in a new line, I'd try to show them in the same
        line as the browse input."

        So: ONE line on a desktop, the search and the "Show" chips, and the
        order is the column headers'. On a phone the search has the first line
        to itself, and under it the chips and an Ordenar BUTTON — square
        corners, an icon and a word — that opens the drawer of orders. The
        button is always drawn on a phone, so the second line is there before
        the shop's answer brings the chips: nothing below moves when they land.
      */}
      <div className="sh-filters">
        <div className="sh-filters__search">
          {/* Browse's typeahead over the same box, so a card is found by its
              name in any language we hold: picking one searches its English
              name, which is what the stock search reads. Typing alone still
              searches what was typed. The list is the catalogue's, narrowed
              to the section's game when it shows one. */}
          <CardAutocomplete
            Field={SearchField}
            label={undefined}
            placeholder={t("Find a card")}
            value={term}
            game={scope.length === 1 ? scope[0] : undefined}
            onChange={search}
            onPick={pickCard}
          />
        </div>
        {/* Only for a shop connected to CardTrader — the shell already knows,
            from the same answer as the shop — or one that has it on. Two
            chips, of which one is always pressed: "All" is what the chip
            being off looks like, said, rather than a toggle you have to know
            is one. */}
        {cardtrader || onlySynced ? (
          <div className="sh-filters__show" role="group" aria-label={t("Show")}>
            <span className="cbm-label sh-filters__label" aria-hidden="true">{t("Show")}</span>
            <Tag selected={!onlySynced} onClick={() => { if (onlySynced) showSynced(false); }}
                 data-cbm="all-filter">
              {t("All")}
            </Tag>
            <Tag icon="refresh-cw" selected={onlySynced} onClick={() => { if (!onlySynced) showSynced(true); }}
                 title={t("Synchronized with CardTrader")} data-cbm="synced-filter">
              {t("Synced")}
            </Tag>
          </div>
        ) : null}
        <span className="sh-filters__sort" ref={sortOpener}>
          <Button variant="outline" size="lg" iconLeft="arrow-down-wide-narrow" aria-haspopup="dialog"
                  aria-label={t("Sort: {order}", { order: t((STOCK_SORTS.find((s) => s.key === sort) || STOCK_SORTS[0]).label) })}
                  data-cbm="stock-sort-open" onClick={openSorting}>
            {t("Sort")}
          </Button>
        </span>
      </div>
      <ModalHost>
        {sorting ? (
          /*
           * A DRAWER FROM THE BOTTOM, made of the app's own dialog: the host
           * locks the page behind and stands the Android pull-to-refresh down,
           * as it does for every dialog (market-price.spec.ts). Picking an
           * order IS the action, so there is no footer to confirm it with.
           *
           * The dialog centres its panel inside a padded scrim; to sit on the
           * bottom edge, full width, the panel takes that padding back.
           *
           * Wrapped in a box of its own because the dialog's scrim carries no
           * class: the wrapper is what app.css animates (`.sh-sortsheet-host`),
           * the scrim fading and the sheet sliding: `is-open` brings both in,
           * and taking it away sends both out.
           */
          <div ref={sortHost} className={`sh-sortsheet-host${sorting === 'open' ? ' is-open' : ''}${sorting === 'closing' ? ' is-closing' : ''}`}>
          <Dialog open role="dialog" aria-label={t("Sort by")} title={t("Sort by")}
                  /* Its own entry (`pushOverlayEntry` above), timed to its slide; not a second one. */
                  backCloses={false}
                  closeLabel={t("Close")} onClose={closeSorting}
                  className="sh-sortsheet" data-cbm="stock-sort-sheet"
                  style={{
                    alignSelf: 'flex-end',
                    width: 'calc(100% + 2 * var(--space-6))',
                    maxWidth: 'none',
                    margin: '0 calc(-1 * var(--space-6)) calc(-1 * var(--space-6))',
                    borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
                  }}>
            <Radio name="stock-sort" value={sort} style={{ gap: 0 }} aria-label={t("Sort by")}
                   onChange={(key) => {
                     /* An arrow key on the radio still under focus during the exit is not a second pick. */
                     if (sorting === 'closing') return;
                     sortBy(key); closeSorting();
                   }}
                   options={STOCK_SORTS.map((s) => ({ value: s.key, label: t(s.label) }))} />
          </Dialog>
          </div>
        ) : null}
      </ModalHost>

      {/*
        ALWAYS DRAWN, ABOVE THE TABLE, DISABLED UNTIL THERE IS SOMETHING TO DO.

        Reported: "I see follow cardmarket shows when I select at least one
        card. That's really hidden so nobody would know the feature exists. I
        would maybe always show the bar with the buttons and all and just
        disable the ones that can't be used or hide them. Maybe even move the
        bar to the top, above the table."

        It was a FIXED bar that floated in only once a row was ticked, which is
        why it had to hide — a permanent overlay would have sat on top of the
        stock. So it is in the flow now, where it can simply stay. A shop that
        has never ticked a box can see that prices can be changed in bulk and
        that listings can follow Cardmarket, which is the whole point: the
        feature a shop most wants was behind a gesture nobody had a reason to
        make.

        DISABLED RATHER THAN HIDDEN, of the two he offered. A greyed button
        still says what is possible here; a hidden one teaches nothing and is
        how this got lost in the first place. The line beside them says what to
        do about it, so the disabling is never a dead end.

        "Clear" is the exception and is only drawn with a selection — it is an
        undo, and an undo for nothing is not a control.
      */}
      {/*
        NOTHING WHERE THE PROMPT WAS. "Tick cards to change them together"
        should go — and it should: the buttons beside it are already greyed,
        which says the same thing without a sentence explaining the obvious to
        somebody who has used a table before. The count is information rather
        than instruction, and design D draws it from nought.

        THE SELECT-ALL BOX IS IN THE BAR ON A PHONE (design D's phone), where
        the table's head row is not the place a thumb looks; on a desktop it is
        the head of the tick-box column. One of the two is drawn at any width
        (app.css), so the box a test or a screen reader finds is always one.
      */}
      <BulkBar data-cbm="stock-bulk" count={selected}
        lead={(
          <input type="checkbox" className="sh-check sh-bulk__all" checked={allOn} disabled={!rows.length}
            ref={(el) => { if (el) el.indeterminate = someOn; }}
            onChange={toggleAll} aria-label={t("Select everything in the games shown")} />
        )}
        extra={selected
          ? <button type="button" className="cbm-bulk__clear" onClick={clear}>{t("Clear")}</button>
          : null}
        note={takeDownWhy || pricedElsewhereWhy}>
          <Button variant="outline" disabled={busy || !selected || pricedElsewhere} onClick={() => setDialog('price')}>{t("Change the price")}</Button>
          {/*
            The mark rather than the word, for the reason it is used on a
            listing: "Cardmarket" and "Cardboardmarket" are the same shape read
            quickly, and this button asks a shop to hand its prices to one of
            them. `alt` carries the name, because here the logo IS the name.
          */}
          {/*
            THE STACKED MARK HERE TOO. The wordmark beside a word in a small
            button is the same problem the rows had: 341×48 shrunk to 12px tall
            is 85px of letters nobody can read, and it made the button twice the
            width of every other one in the bar.
            `alt` still carries the name — the logo IS the name here, which is
            why this reads as "Follow Cardmarket" to a screen reader and in the
            tests that press it.
          */}
          <Button variant="outline" disabled={busy || !selected || pricedElsewhere}
                  onClick={() => setDialog('follow')}>
            {t("Follow")} <CardmarketMark stacked height={16} />
          </Button>
          {/*
            OFF IS AS EASY AS ON. Reported in the same breath as the row marker:
            a shop should have "a way to disable them in bulk just like enabling
            them". A switch somebody can only throw one way is a trap, and this
            one moves money nightly.

            No dialog on this side, deliberately: stopping is the safe
            direction. The price keeps whatever it last landed on and becomes
            editable again, which is what the dialog's own closing line
            promises — so asking twice would be ceremony.
          */}
          <Button variant="outline" disabled={busy || !selected}
                  onClick={() => {
                    setBusy(true);
                    setFollowingInBulk({ ...selection(), followsMarket: false })
                      .then(clear)
                      .catch(() => undefined)
                      .finally(() => setBusy(false));
                  }}>
            {t("Stop following")}
          </Button>
          {/* The reason on the wrapper, not the button: a disabled button is not a thing a pointer hovers, as far as a tooltip is concerned. */}
          <span title={takeDownWhy || undefined}>
            <Button variant="outline" disabled={busy || !canTakeDown} onClick={() => setDialog('down')}>
              {t("Take them down")}
            </Button>
          </span>
      </BulkBar>

      {myCardsState === 'error' ? (
        <EmptyState icon="alert-triangle" title={t("We could not load your stock")}
          description={t("Something went wrong at our end. It is still there.")}
          action={<Button onClick={() => loadMine()}>{t("Try again")}</Button>} />
      ) : rows.length ? (
        /*
         * WAITING WITHOUT MOVING. A chip on the scope bar or a word in the
         * search box asks the server for a narrower stock, and until it
         * answers the table keeps the rows it had, dimmed and marked busy —
         * the same shape, so nothing jumps under the finger that just tapped.
         * A blank or a spinner here would be a layout the answer then undoes.
         */
        <div className="sh-table sh-waits" role="table" aria-label={t("Stock")} aria-busy={myCardsState === 'loading'}>
          <div className="sh-row sh-row--head" role="row">
            <span className="sh-cb" role="columnheader">
              <input type="checkbox" className="sh-check sh-head-all" checked={allOn}
                ref={(el) => { if (el) el.indeterminate = someOn; }}
                onChange={toggleAll} aria-label={t("Select everything in the games shown")} />
            </span>
            {sortHead("Card", ['name', 'name_desc'])}
            {sortHead("Cond.", ['condition', 'condition_desc'], 'sh-c-cond')}
            {/*
              THE LANGUAGE, which a shop's table was the only listing surface
              without one. Noticed while talking about duplicates: "Saying this
              made me realize we're missing the language in the shops' stock
              table." Two rows of the same card in the same condition are only
              tellable apart by it, so a table that hides it shows what looks
              like the same card twice at two prices.

              Beside the condition because they answer together — which copy of
              this card is this — and because the two chips read as one fact.
            */}
            <span className="cbm-label sh-c-lang" role="columnheader">{t("Lang.")}</span>
            <span className="cbm-label sh-c-qty" role="columnheader">{t("Qty")}</span>
            {sortHead("Price", ['price_asc', 'price_desc'])}
            <span className="cbm-label sh-c-status" role="columnheader">{t("Looking")}</span>
            {sortHead("Listed", ['newest', 'oldest'], 'sh-c-age')}
            {/* No caption: a column of pencils does not need one, and the
                buttons under it name the card they edit. */}
            <span className="sh-c-edit" aria-hidden="true" />
          </div>
          {rows.map((c) => {
            const on = all || picked.has(c.id);
            const days = daysOnShelf(c.createdAt);
            return (
              <div key={c.id} className="sh-row" role="row" data-on={on ? '1' : '0'} data-listing={c.id}>
                <span className="sh-cb">
                  <input type="checkbox" className="sh-check" checked={on}
                    onChange={(e) => toggle(c.id, e.target.checked)}
                    aria-label={t("Select {card}", { card: c.title })} />
                </span>
                <div className="sh-c-card">
                  {/*
                    A REAL LINK, so it can be copied and opened in a new tab.
                    The owner: "Each row in the stock section of a shop should be
                    available as a link to copy or open in a new tab. In general,
                    things that can be a real usable link, should be one."

                    It was a `<button>`, which looks identical and is not: no
                    address to copy, no middle click, no ctrl-click, nothing for
                    the browser to offer on a long press. `Linked` is the same
                    navigation with an `href` under it, and it is what browse has
                    used all along — `go.open` IS the app-level `go` this calls.
                  */}
                  <div className="sh-c-title">
                    <Linked to={{ name: 'listing', id: c.id }}>
                      <span className="cbm-dash__title">{c.title}</span>
                    </Linked>
                    {/*
                      WHICH ROWS THE SYNC OWNS, at a glance. The owner,
                      2026-10-20: "an indicator per row in the shop's stock
                      list that easily identifies which listings are
                      synchronized." Beside the title rather than in the price's
                      mark slot: that slot is as wide as Cardmarket's mark, and
                      a wider word there would move the column. The title
                      shortens to make room; the row does not grow.

                      THE ICON ALONE, everywhere. The owner, 2026-10-21: "should
                      only be the icon. For now, we don't have any other platform
                      to synchronize from. Maybe I'd add a tooltip saying
                      'Synchronized with CardTrader'." The word said nothing the
                      icon does not while CardTrader is the only source, and it
                      cost the name its room (50 px of a 183 px title on a
                      phone). The tooltip names it on hover; the label names it
                      to a screen reader and to a touch, where nothing hovers.
                    */}
                    {c.pricing && c.pricing.managedOnCardtrader ? (
                      <Tooltip label={t("Synchronized with CardTrader")} className="sh-synced">
                        <Badge tone="neutral" icon="refresh-cw" data-cbm="synced-badge"
                               role="img" aria-label={t("Synchronized with CardTrader")}
                               title={t("Synchronized with CardTrader")} />
                      </Tooltip>
                    ) : null}
                  </div>
                  <div className="cbm-meta" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {gameLabel(c.game)}{c.set ? ` · ${c.set}` : ''}
                    <span className="sh-mob-inline"> · {c.condition}{c.language ? ` · ${c.language.toUpperCase()}` : ''} · ×{(c.stock && c.stock.quantity) || 1}</span>
                  </div>
                </div>
                <span className="sh-c-cond">
                  <ConditionChip grade={c.condition} label={t(GRADE_NAMES[c.condition] || c.condition)} />
                </span>
                {/* The code, not the name: "ES" fits the column and "Español"
                    does not, and it is the form printed on the card itself. The
                    full name is the title, for anybody who needs it. */}
                <span className="sh-c-lang" title={c.language ? t(CARD_LANGUAGE_NAMES[c.language] || c.language) : undefined}>
                  {c.language ? c.language.toUpperCase() : '—'}
                </span>
                <span className="sh-c-qty sh-num">{(c.stock && c.stock.quantity) || 1}</span>
                {/*
                  A card for trade has no price to edit, and a card following
                  Cardmarket has one the nightly statement owns — typing over
                  either is a number that would not survive the night.
                */}
                {c.priceCents == null ? (
                  <span className="sh-num" style={{ color: 'var(--text-faint)' }}>{t("Trade")}</span>
                ) : (
                  /*
                    THE MARK SAYS WHY THE BOX IS LOCKED.
                    Reported: "cards that have the follow the price enabled
                    should be marked some way in the stock rows". They were —
                    by the price box being disabled, which is a symptom rather
                    than a reason, and the one thing a shop would do about it is
                    hunt for whatever broke it. Their wordmark is the answer in
                    the width available, and it is the vocabulary the bar and
                    the listing already use.
                  */
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    {/* A synced card's price is CardTrader's, and the sweep writes
                        it back within minutes: shown, not offered as a field, and
                        the details dialog says why. */}
                    <PriceCell listing={c}
                               disabled={!!(c.pricing && c.pricing.followsMarket)}
                               readOnly={!!(c.pricing && c.pricing.managedOnCardtrader)}
                               title={c.pricing && c.pricing.managedOnCardtrader ? t("Managed on CardTrader") : undefined}
                               onCommit={(cents) => editListing(c.id, { priceCents: cents }).catch(() => undefined)} />
                    {/*
                      THE SLOT IS ALWAYS THERE, MARK OR NO MARK.

                      Asked for: "reserve the space even if the feature is not
                      active in a row to keep everything aligned". Without it the
                      price boxes sat at two different widths down the column —
                      wider on the rows with nothing in them — and a column that
                      moves depending on a setting is harder to read down than
                      one with a gap in it.

                      `visibility` rather than a conditional, because the space
                      has to be reserved by something of exactly the mark's size,
                      and the mark is the only thing that is reliably that. An
                      empty span with a hard-coded width would be a second place
                      to change when the mark's height changes.
                    */}
                    {/*
                      A BUTTON WHEN IT IS SHOWING, and inert when it is only
                      holding the space. The hidden copy must not be reachable by
                      tab or by a screen reader — it is furniture.
                    */}
                    {c.pricing && c.pricing.followsMarket && !c.pricing.managedOnCardtrader ? (
                      <button type="button" className="sh-mark" onClick={() => setRule(c)}
                              title={t("See how this price follows Cardmarket")}
                              aria-label={t("See how the price of {card} follows Cardmarket", { card: c.title })}>
                        <CardmarketMark stacked height={18} alt="" />
                      </button>
                    ) : (
                      <span className="sh-mark" style={{ visibility: 'hidden' }} aria-hidden="true">
                        <CardmarketMark stacked height={18} alt="" />
                      </span>
                    )}
                  </span>
                )}
                {/* How many traders have it on a list — the owner's column. */}
                <span className="sh-c-status sh-num">{c.wantedBy ? c.wantedBy : '—'}</span>
                <span className="sh-c-age sh-num">{days === 0 ? t("today") : t("{n} d", { n: days })}</span>
                {/*
                  EVERYTHING THAT IS NOT THE PRICE.
                  Reported: "in the stock section of a shop there's only a way of
                  modifying the price, but not anything else like condition or
                  quantity, the possibility of adding photographs".
                  All of it already existed, in the Selling tab's own dialog —
                  condition excepted, which the API refused until now. A shop
                  simply never had a way to open it. So this opens THAT dialog
                  rather than growing a second one beside it: two editors for one
                  listing is how the two drift apart.
                */}
                <span className="sh-c-edit">
                  {/* A synced card opens read-only details (ticket 11), and the
                      button says so rather than promising an edit. */}
                  {c.pricing && c.pricing.managedOnCardtrader ? (
                    <IconButton icon="eye" size="sm" variant="ghost"
                      label={t("See {card}", { card: c.title })}
                      onClick={() => setEditing(c)} />
                  ) : (
                    <IconButton icon="pencil" size="sm" variant="ghost"
                      label={t("Edit {card}", { card: c.title })}
                      onClick={() => setEditing(c)} />
                  )}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState icon="layers"
          title={term ? t("Nothing matches") : t("No stock yet")}
          description={term
            ? t("Nothing here fits that.")
            : t("Post a card or import a list. It shows on the map straight away.")}
          action={term
            ? <Button variant="outline" onClick={() => search('')}>{t("Clear the search")}</Button>
            : <Button onClick={() => go.open('post')}>{t("Post a card")}</Button>} />
      )}

      {cursors.mine ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--space-6)' }}>
          <Button variant="outline" disabled={loadingMore === 'mine'} onClick={() => loadMore('mine')}>
            {loadingMore === 'mine' ? t("Loading…") : t("Show more")}
          </Button>
        </div>
      ) : null}


      {dialog === 'price' ? (
        <BulkPriceDialog count={selected} onClose={() => setDialog(null)} onApply={applyPrices} />
      ) : null}
      {dialog === 'follow' ? (
        <BulkFollowMarketDialog
          count={selected}
          onClose={() => setDialog(null)}
          onApply={(market) => {
            setDialog(null);
            setBusy(true);
            /*
             * The whole setting travels, not just the switch: basis and
             * adjustment are what the dialog was added to ask for, and sending
             * `followsMarket` alone would have made the form decoration.
             *
             * `marketBasis` and `marketAdjust`, NOT `basis` and `adjust`. The
             * control names them one way and the route the other, and the route
             * refuses an unknown property outright — so the first version of
             * this sent a body that came back 400 and nothing followed
             * anything. `you.jsx` has had the right shape all along; it is the
             * one to copy, not the control's own state names.
             */
            setFollowingInBulk({
              ...selection(),
              followsMarket: true,
              marketBasis: market.basis,
              marketAdjust: market.adjust,
            })
              .then(clear)
              // Said by the store, which also reloads the rows, and said better
              // there than a second copy here would say it.
              .catch(() => undefined)
              .finally(() => setBusy(false));
          }} />
      ) : null}
      {rule ? (
        <MarketRuleDialog
          listing={rule}
          onClose={() => setRule(null)}
          onApply={(market) => {
            const id = rule.id;
            setRule(null);
            editListing(id, {
              followsMarket: market.followsMarket,
              ...(market.followsMarket
                ? { marketAdjust: market.adjust, marketBasis: market.basis }
                : {}),
            })
              // Said by the store, which also reloads the rows.
              .catch(() => undefined);
          }} />
      ) : null}
      {editing ? (
        <ModalHost>
          <EditListingDialog listing={editing} onClose={() => { setEditing(null); loadMine({ q: searched }); }} />
        </ModalHost>
      ) : null}
      {dialog === 'down' ? (
        /* The shared confirmation (chrome.jsx); its wording and its red button as they were. */
        <ConfirmDialog data-cbm="stock-take-down"
          title={t("Take down {n} listings?", { n: chosen.length })}
          description={t("They come off the market straight away. The cards stay in your stock.")}
          cancelLabel={t("Keep them")} confirmLabel={t("Take them down")} busyLabel={t("One moment…")}
          confirmVariant="danger" busy={busy}
          onCancel={() => setDialog(null)} onConfirm={takeDown} />
      ) : null}
    </>
  );
}

/* ---------------------------------------------------------------- requests */

/**
 * THE DEAL IN ONE LINE, from the shop's side: what comes in against the card
 * that goes out. As an English key and its values, so it is translated where
 * it is shown. `under` is the percentage below the listed price, or null.
 *
 * Three shapes, the design's: a buy at the full price, a cash offer under it,
 * and a trade of cards with or without cash on top. The cash can run either
 * way since migration 065, so the shop paying is said rather than assumed
 * away. Null where nothing comes in at all — no line beats a wrong one.
 */
function requestSummary(x) {
  const { items, cash, agreedPrice } = x.offer;
  const cards = items.filter((i) => i.direction === 'in').length;
  const theyPay = cash && !cash.youPay ? cash.cents : 0;
  const youPay = cash && cash.youPay ? cash.cents : 0;
  const money = (c) => api.formatCents(c);
  if (cards) {
    const one = cards === 1;
    if (theyPay) return { key: one ? "Trades 1 card + {cash}" : "Trades {n} cards + {cash}", params: { n: cards, cash: money(theyPay) }, under: null };
    if (youPay) return { key: one ? "Trades 1 card, you add {cash}" : "Trades {n} cards, you add {cash}", params: { n: cards, cash: money(youPay) }, under: null };
    return { key: one ? "Trades 1 card" : "Trades {n} cards", params: { n: cards }, under: null };
  }
  if (!theyPay) return null;
  const listed = agreedPrice ? agreedPrice.totalCents : null;
  if (listed == null) return { key: "Offers {cash}", params: { cash: money(theyPay) }, under: null };
  if (theyPay === listed) return { key: "Buys at {price} · full price", params: { price: money(theyPay) }, under: null };
  if (theyPay > listed) return { key: "Buys at {price}", params: { price: money(theyPay) }, under: null };
  return {
    key: "Offers {cash} for {listed} listed",
    params: { cash: money(theyPay), listed: money(listed) },
    under: Math.round(((listed - theyPay) / listed) * 100),
  };
}

/**
 * A CARD IN A REQUEST, AS A REAL LINK TO ITS LISTING — the owner: "There should
 * be an easy way to go to the listing." Opened inside the rail with Requests
 * still lit (`?sec=requests`, read by the shell), because that is where the
 * shop came from. Plain text where the card is on no listing.
 */
function ListingLink({ id, children }) {
  if (!id) return children;
  return (
    <Linked to={{ name: 'listing', id, sec: 'requests' }}>
      <span className="sh-req__link">
        {children}
        <span aria-hidden="true" style={{ display: 'inline-flex' }}><Icon name="arrow-up-right" size={14} /></span>
      </span>
    </Linked>
  );
}

/**
 * ONE REQUEST, READ BEFORE IT IS ANSWERED — the owner, 2026-10-17: "An Accept
 * or Decline button without information on what we're acting on does not
 * work." The design's row: the card, its condition and its price; who; the
 * deal in a line; the cards offered; then how far, the arrangement, and when it
 * came in. Everything is in the trade as loaded, so nothing arrives later to
 * push the row down.
 *
 * NOT DRAWN, from the design: the verified mark beside the name (verification
 * is parked and out of the interface), the trader's note and "wants <slot>"
 * (no such data), and the tick box (bulk answers are not built).
 */
function RequestRow({ x, busy, showGame, onAnswer, onOpen, selected }) {
  const { messageTrader, t } = useApp();
  const yours = x.offer.items.filter((i) => i.direction === 'out');
  const offered = x.offer.items.filter((i) => i.direction === 'in');
  const card = yours[0] || null;
  const unit = x.offer.agreedPrice ? x.offer.agreedPrice.unitCents : null;
  const deal = requestSummary(x);
  return (
    /*
     * THE ROW ITSELF OPENS THE TRADE too, anywhere that is not one of its own
     * buttons or links; the arrow is the same thing for a keyboard.
     */
    <div className="sh-req" data-trade={x.id} data-selected={selected ? '1' : undefined}
         onClick={(e) => { if (!e.target.closest('button, a, input, label')) onOpen(x); }}>
      <Icon name="arrow-left-right" size={18} style={{ marginTop: 3 }} />
      <div className="sh-req__main">
        <div className="sh-req__card">
          {card ? <ListingLink id={card.listingId}><span className="sh-req__title">{card.title}</span></ListingLink> : null}
          {x.quantity > 1 ? <span className="sh-num">×{x.quantity}</span> : null}
          {card && card.grade ? <ConditionChip grade={card.grade} label={t(GRADE_NAMES[card.grade] || card.grade)} /> : null}
          <span className="sh-num">{unit != null ? api.formatCents(unit) : t("For trade")}</span>
          {showGame && x.game ? <Tag tone="neutral">{gameLabel(x.game)}</Tag> : null}
        </div>
        <div className="sh-req__who">{x.withWhom}</div>
        {deal ? (
          <div className="sh-req__deal">
            <span>{t(deal.key, deal.params)}</span>
            {deal.under ? <Badge tone="caution">{t("{n}% under", { n: deal.under })}</Badge> : null}
          </div>
        ) : null}
        {offered.length ? (
          <ul className="sh-req__offered" aria-label={t("Offered")}>
            {offered.map((i) => (
              <li key={i.id || i.title}>
                <ListingLink id={i.listingId}><span>{i.title}</span></ListingLink>
                {i.grade ? <ConditionChip grade={i.grade} label={t(GRADE_NAMES[i.grade] || i.grade)} /> : null}
              </li>
            ))}
          </ul>
        ) : null}
        <div className="sh-req__meta cbm-meta">
          <DistanceTag km={x.km} floorLabel={t("less than 1 km")} />
          {/*
            No hour, and none missing: a trade at this counter happens while
            the shop is open. The amber "no time yet" belonged to the rule that
            asked a trader to pick a slot inside the opening hours, which is the
            rule this replaced.
          */}
          {x.when
            ? <span>{x.when}</span>
            : x.atCounter
              ? <span>{t("While you are open")}</span>
              : <Badge tone="caution">{t("No time yet")}</Badge>}
          {x.place && !x.atCounter ? <span>{x.place}</span> : null}
          {x.createdAt ? <span>{t("Came in {when}", { when: formatWhen(x.createdAt) })}</span> : null}
        </div>
        {/* Where its copies stand on CardTrader, once accepting has written
            there — the same line the trade's own card carries, on whichever
            tab the row is under: an accepted one still to be marked done is
            under Waiting on you, and one cancelled after accepting under Past,
            where "put back as a new product" is still worth reading. It says
            nothing for a trade write-back never touched. */}
        <CardTraderLine trade={x} style={{ marginTop: 'var(--space-2)', marginBottom: 0 }} />
      </div>
      <div className="sh-req__act">
        {/*
          WHAT THE SERVER SAYS THE SHOP CAN DO, not which tab the row is in.
          "Waiting on you" holds both a request to answer and an accepted trade
          to mark done — what the badge has always counted — and each row
          offers its own next step. Accepted and marked done on this side, or
          sent by the shop and not yet answered, it waits for the other, and
          says for whom, as the trade's card does; a sent one can still be
          withdrawn. Over, it says how it ended — the words the trader's own
          trade cards use, where a declined trade also reads Cancelled.
        */}
        {x.can && x.can.accept ? (
          <>
            <Button size="sm" disabled={busy} onClick={() => onAnswer(x, 'accept')}>
              {busy ? t("One moment…") : t("Accept")}
            </Button>
            {/* The x, as in the trade's own actions (`TradeActions`); its name is the tooltip. */}
            <IconButton icon="x" size="sm" variant="ghost" disabled={busy} label={t("Decline")}
              onClick={() => onAnswer(x, 'decline')} />
          </>
        ) : x.can && x.can.complete ? (
          <Button size="sm" iconLeft="check" disabled={busy} onClick={() => onAnswer(x, 'complete')}>
            {busy ? t("One moment…") : t("Mark done")}
          </Button>
        ) : x.status === 'accepted' || x.status === 'proposed' ? (
          <>
            <Badge tone="caution">{t("Waiting for {name}", { name: x.them.displayName.split(' ')[0] })}</Badge>
            {x.status === 'proposed' && x.can && x.can.cancel ? (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => onAnswer(x, 'cancel')}>
                {busy ? t("One moment…") : t("Withdraw")}
              </Button>
            ) : null}
          </>
        ) : x.status === 'completed' ? (
          <Badge tone="verified" icon="check">{t("Completed")}</Badge>
        ) : isPast(x) ? <Badge tone="neutral">{t("Cancelled")}</Badge> : null}
        {/* The conversation with them, which is where this trade already sits. */}
        <IconButton icon="message-circle" size="sm" variant="ghost"
          label={t("Message")} onClick={() => messageTrader(x.them.id, x.withWhom)} />
        {/*
          THE ARROW OPENS THIS TRADE, here in Requests: beside the list on a
          desktop, as a page on a phone (`RequestDetail`). It used to leave for
          `/trades?id=…`, the trader's screen inside the rail.
        */}
        <IconButton icon="chevron-right" size="sm" variant={selected ? 'outline' : 'ghost'} active={selected}
          label={t("Open this trade")} onClick={() => onOpen(x)} />
      </div>
    </div>
  );
}

/**
 * ONE TRADE, OPENED FROM ITS ROW, beside the list on a desktop and as a page of
 * its own on a phone. There is no drawn design for this screen (the shop shell
 * in Claude Design stops at the row), so it is the app's own trade card — the
 * one `/trades` and the conversation show — under a head that says who and
 * what, in the row's words. Every action is `TradeActions`, the one
 * implementation of accept, counter, decline, mark done, withdraw and report,
 * with What changed and the CardTrader line above them.
 */
/**
 * The pane's head, the same in every state it can be in — a trade, one on its
 * way, one that is not there: the way back to the list ("Requests" on a phone,
 * where it is the only way out of the page; a cross beside the list).
 */
function PaneHead({ wide, onClose }) {
  const { t } = useApp();
  return (
    <div className="sh-pane__head">
      {wide
        ? <span className="cbm-label">{t("This trade")}</span>
        : <Button variant="ghost" size="sm" iconLeft="arrow-left" onClick={onClose} style={{ marginLeft: -10 }}>{t("Requests")}</Button>}
      {wide ? <IconButton icon="x" size="sm" variant="ghost" label={t("Close")} onClick={onClose} /> : null}
    </div>
  );
}

function RequestDetail({ x, onClose, wide, outside }) {
  const { messageTrader, t } = useApp();
  const deal = requestSummary(x);
  return (
    <div className="sh-pane__body" data-pane={x.id}>
      <PaneHead wide={wide} onClose={onClose} />
      {/*
        NOT IN THE LIST BESIDE IT — another game than the one showing, or
        older than the list goes back — and opened anyway, because it was
        asked for by name. Said, so the list not holding it is not a mystery.
      */}
      {outside ? (
        <p className="sh-pane__note" data-cbm="outside-the-list">
          {t("Not in the list you are looking at — another game, or older. Shown because it was asked for.")}
        </p>
      ) : null}
      {deal ? (
        <div className="sh-req__deal" style={{ marginBottom: 'var(--space-4)' }}>
          <span>{t(deal.key, deal.params)}</span>
          {deal.under ? <Badge tone="caution">{t("{n}% under", { n: deal.under })}</Badge> : null}
        </div>
      ) : null}
      <MeetupCard
        place={x.place || t("Not agreed yet")}
        address={x.address}
        when={x.when || (x.atCounter ? t("While you are open") : t("Time not agreed yet"))}
        withWhom={x.withWhom}
        items={x.items}
        status={x.cardStatus}
        statusLabel={t(MEETUP_STATUS_NAMES[x.cardStatus] || MEETUP_STATUS_NAMES.proposed)}
        action={<TradeActions trade={x} listingSec="requests" onMessage={() => messageTrader(x.them.id, x.withWhom)} />}
      />
    </div>
  );
}

/** Each tab's empty state, held as keys and translated where it is rendered. */
const EMPTY = {
  new: { title: "Nothing waiting on you", description: "A request from a trader nearby shows up here first." },
  agreed: { title: "Nothing agreed yet", description: "A trade you mark done waits here until they mark it done too." },
  sent: { title: "Nothing sent", description: "A trade you propose waits here until they answer." },
  past: { title: "Nothing finished yet", description: "Completed, declined and cancelled trades end up here." },
};

/**
 * The orders waiting on the shop, with the answer on the row.
 *
 * "KNOWING QUICKLY THE ORDERS THEY HAVE PENDING, QUICK WAYS OF ACCEPTING
 * THEM" — the owner's words, and the reason this is rows with Accept on each
 * rather than the trader's card deck. A shop answers twenty of these in a
 * sitting; a trader answers one a week.
 *
 * ACCEPTING TAKES NOTHING ELSE where the proposal named a place and a time —
 * and with a shop it always names the place now, because the counter rule fills
 * it in. That is what makes one tap honest here: the server is not being sent a
 * guess. A proposal with no TIME still cannot be accepted, and this says so
 * rather than inventing one.
 */
/**
 * PAST, A PAGE AT A TIME (ticket 10; the owner's default 5). The open list
 * stops at a hundred and a shop's history does not, so finished trades are
 * asked for by themselves, newest first, and the next page as the shop
 * reaches the end of the last. Asked again from the top when the scope moves
 * or the open list changes — a trade just completed or withdrawn there is the
 * newest thing in Past. `active` is whether Past is the tab on screen: no
 * request is made for a tab nobody is looking at.
 */
function usePastPages(scope, active, trades) {
  const [pages, setPages] = React.useState({ key: null, trades: [], next: null, loading: false, failed: false });
  const key = scope.join(',');
  const games = scope.length ? scope : undefined;
  React.useEffect(() => {
    if (!active) return undefined;
    let live = true;
    setPages((p) => ({ ...p, loading: true }));
    api.pastTrades({ game: games })
      .then((r) => {
        if (!live) return;
        const first = r.trades.map(api.toTradeView);
        /*
         * THE TOP AGAIN, THE REST KEPT. Asked again because something on the
         * open list changed — usually a trade that has just finished, which is
         * the newest thing here — so the first page is merged over what was
         * read, not swapped for it: a shop three pages down keeps its pages,
         * its place and the trade it has open.
         */
        setPages((p) => {
          if (p.key !== key) return { key, trades: first, next: r.next, loading: false, failed: false };
          const fresh = new Set(first.map((x) => x.id));
          return { ...p, trades: [...first, ...p.trades.filter((x) => !fresh.has(x.id))], loading: false, failed: false,
            next: p.next === null && p.trades.length <= first.length ? r.next : p.next };
        });
      })
      /* Said, never an endless "Loading…": see `failed` where Past is drawn. */
      .catch(() => { if (live) setPages((p) => (p.key === key ? { ...p, loading: false } : { key, trades: [], next: null, loading: false, failed: true })); });
    return () => { live = false; };
  }, [active, key, trades]);
  /* Read through a ref, so `more` keeps one identity and the end-of-list observer is made once. */
  const latest = React.useRef(pages);
  latest.current = pages;
  const more = React.useCallback(() => {
    const p0 = latest.current;
    if (!p0.next || p0.loading) return;
    const before = p0.next;
    setPages((p) => ({ ...p, loading: true }));
    api.pastTrades({ game: games, before })
      .then((r) => setPages((p) => (p.next === before
        ? { ...p, trades: [...p.trades, ...r.trades.map(api.toTradeView)], next: r.next, loading: false }
        : { ...p, loading: false })))
      .catch(() => setPages((p) => ({ ...p, loading: false })));
  }, [key]);
  const current = pages.key === key;
  return {
    trades: current ? pages.trades : [],
    next: current ? pages.next : null,
    loading: pages.loading || !current,
    failed: current && pages.failed,
    more,
  };
}

/** Asks for the next page when it comes into view, which is when the shop has reached the end. */
function WhenReached({ onReach }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const seen = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) onReach(); });
    seen.observe(el);
    return () => seen.disconnect();
  }, [onReach]);
  return <div ref={ref} aria-hidden="true" style={{ height: 1 }} />;
}

function ShopRequests({ go, scope = [], route = {} }) {
  const { trades, tradesState, loadTrades, actOnTrade, notify, myCardsByGame, pastTotal, t } = useApp();
  /*
   * THE GAME ON A ROW ONLY WHERE IT TELLS ONE ROW FROM ANOTHER: a scope of
   * several games, or All games at a shop that stocks more than one. Under a
   * single game every row would say the same word.
   */
  const stocked = Object.keys(myCardsByGame || {}).filter((g) => (myCardsByGame || {})[g]);
  const showGame = scope.length > 1 || (scope.length === 0 && stocked.length > 1);
  /*
   * THE TAB IS IN THE ADDRESS (`?tab=agreed`), so a listing opened from a row
   * comes Back to the tab it was opened from rather than to the first one.
   * Pushed, as every tab is (owner, 2026-10-22: "let's unify navigation
   * behaviors"): Back steps to the tab you were on.
   */
  const tab = ['agreed', 'sent', 'past'].includes(route.tab) ? route.tab : 'new';
  const tabParam = (value) => (value === 'new' ? undefined : value);
  /*
   * RELOADED, WITH THE SCOPE, after an accept from a row or from its dialog.
   * The store remembers the games the shell set (see `loadTrades`), so the
   * list a shop goes on answering from is the narrowed one it was reading,
   * with whatever arrived meanwhile in it — and never the whole counter back
   * again under a Magic chip.
   */
  const accepted = () => { notify(t("Accepted.")); loadTrades(); };
  const { accept: acceptOne, dialog: acceptDialog } = useAccepting({ onAccepted: accepted });
  const [confirm, confirmDialog] = useConfirm();
  const setTab = (value) => go.to('requests', { tab: tabParam(value), id: route.id });
  const [busyId, setBusyId] = React.useState(null);

  /*
   * ONE TRADE OPEN, NAMED IN THE ADDRESS (`?id=`), so it can be sent to
   * somebody and Back works. The pane shows it whichever tab is lit: Mark done
   * in the pane moves the trade to Agreed, and the trade being read must not
   * vanish from under the hand that pressed the button.
   *
   * WIDE is the shell's own desktop width, the one its rail appears at.
   */
  const wide = useMedia('not all and (max-width:1000px)');
  const pastPages = usePastPages(scope, tab === 'past', trades);
  /* Held if either list holds it: the open trades, or the Past pages read so far. */
  const listed = route.id
    ? trades.find((x) => x.id === route.id) || pastPages.trades.find((x) => x.id === route.id) || null
    : null;
  /*
   * A TRADE THE LIST DOES NOT HOLD — another game than the scope, or older
   * than the hundred the list returns — is asked for by itself and shown
   * anyway, with a note, rather than widening the scope or opening nothing.
   * Asked again whenever the list changes, which is what every action on a
   * trade does (`actOnTrade` writes the list), so the pane follows an answer
   * given in it. `{ id, trade }`, `trade` null when it is not the shop's.
   */
  const [alone, setAlone] = React.useState(null);
  const fetchAlone = Boolean(route.id && !listed && tradesState !== 'loading');
  React.useEffect(() => {
    if (!fetchAlone) return undefined;
    let live = true;
    api.trade(route.id)
      .then((r) => { if (live) setAlone({ id: route.id, trade: api.toTradeView(r.trade) }); })
      /* Only a 404 is "not yours"; a fault on our side or the network is said as one. */
      .catch((e) => { if (live) setAlone({ id: route.id, trade: null, failed: !(e && e.code === 'not_found') }); });
    return () => { live = false; };
  }, [fetchAlone, route.id, trades]);
  const aloneHere = alone && alone.id === route.id && !listed ? alone : null;
  const open = listed || (aloneHere && aloneHere.trade) || null;
  /* Only said when the list did load: with no list, "another game, or older" would not be why. */
  const outside = Boolean(!listed && open && tradesState !== 'error');
  const missing = Boolean(aloneHere && !aloneHere.trade);
  /*
   * NAMED BUT NOT LOADED YET — a link, or Today's row, arriving before the
   * list, or a trade outside it on its way: the pane (and on a phone the
   * page) takes the trade's shape at once rather than showing the list or
   * "open a trade" and then flipping.
   */
  const pending = Boolean(route.id && !open && !missing);
  /*
   * THE LIST KEEPS ITS PLACE. Every navigation scrolls the window to the top
   * (the router's rule), which on a desktop would throw the list back to its
   * first row every time a lower one is opened, and on a phone would bring
   * the shop back from a trade to the top of a long list. So the scroll is
   * taken when a row is opened and given back — at once beside the pane, and
   * on the way back to the list on a phone, where the trade's own page starts
   * at its top. (On the browser's Back the browser restores it as well; this
   * covers the page's own back link too. The desktop half is measured in
   * shop-counter.spec.)
   */
  const listScroll = React.useRef(0);
  const openedHere = React.useRef(false);
  const openTrade = (x) => {
    /*
     * One history entry for the pane, however many trades are opened in it in
     * turn: a second one REPLACES the first, so closing goes back to the list
     * and not to the trade before.
     */
    const swapping = Boolean(route.id);
    if (!swapping) listScroll.current = window.scrollY;
    openedHere.current = openedHere.current || !swapping;
    go.to('requests', { tab: tabParam(tab), id: x.id }, swapping ? { replace: true } : undefined);
  };
  const closeTrade = () => {
    /* Back, when this list opened it, so the browser's own Back agrees; otherwise the list, in place. */
    if (openedHere.current) { openedHere.current = false; window.history.back(); }
    else go.to('requests', { tab: tabParam(tab) }, { replace: true });
  };
  React.useLayoutEffect(() => {
    if (!route.id) openedHere.current = false;
    if (wide || !route.id) window.scrollTo(0, listScroll.current);
  }, [route.id]);

  /*
   * NO LOAD OF ITS OWN. The shell loads the trades on arrival and on every
   * scope change, scoped (see `ShopShell`), and `answer` below reloads after
   * an accept or a decline. A load here was the unscoped one: it fired on
   * mount, before the shell's, and raced it for the list the shop read.
   */
  /*
   * WAITING ON YOU IS WHAT THE BADGE COUNTS, by the same predicate off the
   * same list (`waitsOnYou`). Agreed is what is left of the accepted ones:
   * marked done here, waiting on the other side.
   */
  const waiting = trades.filter(waitsOnYou);
  const agreed = trades.filter((x) => x.status === 'accepted' && !waitsOnYou(x));
  /*
   * EVERY TRADE THE SHOP HAS IS UNDER ONE TAB. Sent: proposed and waiting on
   * the trader — proposed by the shop, or countered by it. Past: over, however
   * it ended. Before these two, a trade the shop proposed, and every finished
   * one, was in neither tab and reachable only through `/trades`.
   */
  const sent = trades.filter((x) => x.status === 'proposed' && !waitsOnYou(x));
  /* The open list holds no finished trades for a shop any more (ticket 10); Past is its own pages. */
  const past = pastPages.trades.filter(isPast);
  const list = { new: waiting, agreed, sent, past }[tab];
  const listWaiting = tab === 'past' ? pastPages.loading && !past.length : tradesState === 'loading' && !trades.length;

  const answer = async (trade, action) => {
    /* Ending one, or marking it done, is asked first, in the words the trade card asks it (`endTradeQuestion`). */
    if ((action === 'decline' || action === 'cancel' || action === 'complete')
      && !(await confirm(endTradeQuestion(trade, action, t)))) return;
    setBusyId(trade.id);
    try {
      /*
       * ACCEPT IS THE HOOK'S (`useAccepting`): one tap, and a proposal with no
       * place or time — which a shop whose counter nobody has checked yet can
       * receive — opens the dialog a trader meets, right here, instead of a
       * refusal. `accepted` below then speaks and reloads, whichever way the
       * accept happened.
       */
      if (action === 'accept') { await acceptOne(trade); return; }
      await actOnTrade(trade.id, action, {});
      /* The toast is one slot, so this replaces what `actOnTrade` said, in the back office's words. */
      if (action === 'decline') notify(t("Declined."));
      loadTrades();
    } catch (e) {
      /* Any refusal is said by `actOnTrade`. */
    } finally { setBusyId(null); }
  };

  /*
   * THE LIST AND THE TRADE SIDE BY SIDE on a desktop, the pane always drawn so
   * opening a trade moves nothing in the list; on a phone one or the other
   * (`data-open`), the list kept mounted and hidden so it comes back as it was.
   */
  return (
    <div className="sh-work" data-open={open || pending || missing ? '1' : undefined}>
      <div className="sh-work__list">
      <SectionHead title={t("Requests")} sub={t("Accepting holds the card for them.")} />
      {/*
        FOUR TABS SCROLL WITHIN THEMSELVES on a phone rather than pushing the
        page sideways; the rule under them spans the row either way. Past has
        no count: a running total of finished trades is not something waiting.
      */}
      <div style={{ marginBottom: 'var(--space-5)', overflowX: 'auto' }}>
        <Tabs label={t("Requests")} value={tab} onChange={setTab} style={{ width: 'max-content', minWidth: '100%' }} items={[
          { value: 'new', label: t("Waiting on you"), count: waiting.length || undefined },
          { value: 'agreed', label: t("Agreed"), count: agreed.length || undefined },
          { value: 'sent', label: t("Sent by you"), count: sent.length || undefined },
          /* Counted with the open list (eventually consistent, the owner's word). */
          { value: 'past', label: t("Past"), count: pastTotal || undefined },
        ]} />
      </div>

      {listWaiting ? (
        <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>{t("Loading…")}</span>
      ) : list.length ? (
        <div className="sh-reqs">
          {list.map((x) => (
            <RequestRow key={x.id} x={x} busy={busyId === x.id} selected={open && open.id === x.id}
              showGame={showGame} onAnswer={answer} onOpen={openTrade} />
          ))}
          {/* The next page of Past, asked for as the end comes into view; the button is the same for a keyboard. */}
          {tab === 'past' && pastPages.next ? (
            <div className="sh-reqs__more">
              <WhenReached onReach={pastPages.more} />
              <Button size="sm" variant="ghost" disabled={pastPages.loading} onClick={pastPages.more}>
                {pastPages.loading ? t("Loading…") : t("Show older trades")}
              </Button>
            </div>
          ) : null}
        </div>
      ) : tab === 'past' && pastPages.failed ? (
        <EmptyState icon="alert-triangle" title={t("We could not load your trades")}
          description={t("Something went wrong at our end, not yours. Nothing has been agreed or cancelled.")} />
      ) : (
        <EmptyState icon="arrow-left-right" title={t(EMPTY[tab].title)} description={t(EMPTY[tab].description)} />
      )}
      </div>
      <aside className="sh-work__pane" aria-label={t("This trade")}>
        {open
          ? <RequestDetail x={open} wide={wide} onClose={closeTrade} outside={outside} />
          : missing ? (
            /* Said, rather than an empty pane or the first tab as if nothing had been asked. */
            <div className="sh-pane__body" data-pane-missing={route.id}>
              <PaneHead wide={wide} onClose={closeTrade} />
              {aloneHere.failed
                ? <EmptyState icon="alert-triangle" title={t("We could not load your trades")}
                    description={t("Something went wrong at our end, not yours. Nothing has been agreed or cancelled.")} />
                : <EmptyState icon="search-x" title={t("This trade is not one of yours")}
                    description={t("The link may be mistyped, or the trade belongs to another account.")} />}
            </div>
          ) : pending ? (
            <div className="sh-pane__body" aria-busy="true">
              <PaneHead wide={wide} onClose={closeTrade} />
              <Skeleton shape="meetup-card" />
              <span role="status" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>{t("Loading…")}</span>
            </div>
          ) : <div className="sh-pane__empty">{t("Open a trade from the list to see all of it here.")}</div>}
      </aside>
      {acceptDialog}
      {confirmDialog}
    </div>
  );
}

/* -------------------------------------------------------------------- shop */

/** What traders see, and the way back to the form that sets it. */
/**
 * THE STORE — its state, and the form that changes it, on one page.
 *
 * Reported: "we show a summary of the shop and a button to change details.
 * Clicking it takes us to another summary with another change the details
 * button and when that one is clicked it takes us to the stepped form... We
 * should skip all the middle things and in the same page show all these
 * information and form to change things."
 *
 * Both middle layers are gone. What stays above the form is the part a summary
 * was genuinely for and a form cannot say: whether the store is live, and the
 * link to the page a trader actually sees. Everything underneath is editable
 * where it is read, which is the whole of his complaint.
 *
 * THE FORM IS THE APPLICATION FORM, in its editing shape rather than a second
 * copy — see `ShopScreen`. One screen means one set of validation rules and one
 * save, so the back office cannot drift away from what applying accepts.
 */
function ShopPage({ shop, go }) {
  const { t } = useApp();
  return (
    <>
      <SectionHead title={t("The store")} sub={t("What traders see when they find you.")} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-5)' }}>
        <span style={{ font: 'var(--type-h3)', color: 'var(--text-strong)' }}>{shop.name}</span>
        {shop.status === 'verified'
          ? <Badge tone="verified" icon="shield-check">{t("Verified store")}</Badge>
          : <Badge tone="neutral" icon="clock">{t("Waiting to be checked")}</Badge>}
        {/*
          ONLY ONCE THE PAGE EXISTS (owner, 2026-10-20; ticket 17): the public
          page shows a shop once it has been checked, so for one still waiting
          this opened "No trader by that name". The same signal as the store
          form's "Your store's page" link (shop.jsx).
        */}
        {shop.status === 'verified' && shop.handle ? (
          <Button variant="outline" size="sm" iconLeft="eye" onClick={() => go.open('trader', { id: shop.handle })}>
            {t("See your page")}
          </Button>
        ) : null}
      </div>
      {/*
        Said once, above the fields, rather than discovered after saving. Only
        the name, the address and the pin send a checked store back into the
        queue — everything else on this page saves without touching the badge.
      */}
      {shop.status === 'verified' ? (
        <p style={{ margin: '0 0 var(--space-5)', font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {t("Changing the name or the address sends it back to be checked.")}
        </p>
      ) : null}
      <ShopScreen editing />
    </>
  );
}

/* ------------------------------------------------------------------- shell */

/**
 * The back office, which replaces the app's own furniture.
 *
 * A SHELL RATHER THAN A SCREEN, and `App.jsx` renders it outside the normal
 * chrome for that reason: the rail IS the navigation for as long as somebody is
 * working as the shop. A header offering Browse and Nearby above a shop's stock
 * table is two products on one page.
 */
/**
 * THE ROUTES A SHOP MAY BE ON, and what each one is called in the rail.
 *
 * Anything absent sends the shop back to its counter — browse, nearby, trades,
 * the profile, the invite page. Not because the links are hidden (they are, but
 * a typed address is a link somebody did not need) but because those screens
 * mean something different to a shop, or nothing at all: "a shop does not need
 * to browse... It's more a trades receiver only."
 */
const EMBEDDED = {
  /*
   * Posting is an ACTION FROM STOCK rather than a place, so the rail keeps
   * Stock lit while it is open; `takesGame` marks it as the one screen the
   * scope hands a game to (see `embeddedProps` below). The importer is the
   * same action, except that it shows nothing from the scope bar (`bar`): it
   * reads the file whole — every row's game comes from the file by its
   * product id (see `cardmarket-import`), so a bar above it would promise a
   * narrowing nothing performs. Not even the label; the design's note under
   * it ("each row keeps the game set in the file") the owner dropped.
   */
  post: { Screen: PostScreen, under: 'stock', takesGame: true },
  import: { Screen: ImportScreen, under: 'stock', bar: false },
  /*
   * A card, opened from the stock table — or from a request, which says so
   * with `?sec=requests` and keeps Requests lit (`secLights`, the sections
   * that may say so): the card may be somebody else's, and the shop came from
   * Requests, not from its stock.
   */
  listing: { Screen: ListingScreen, under: 'stock', secLights: ['requests'] },
  inbox: { Screen: InboxScreen, under: 'inbox' },
  /*
   * NOT `trades`. A shop's trades are its Requests — list, tabs and the trade
   * opened beside them — so the trader's `/trades` screen is not one of its
   * screens (shop trades view, ticket 09; the owner's default 1: people keep
   * theirs). An old `/trades` address is sent to Requests by `fromTrades`.
   */
  /* The application form, which is how the store's own details are changed. */
  shop: { Screen: ShopScreen, under: 'shop' },
  /* Its own public page — what a customer sees, which is a thing a shopkeeper
     checks and the one trader screen that is genuinely about them. */
  trader: { Screen: TraderScreen, under: 'shop' },
};

export function ShopShell() {
  const { trader, actAs, go, route, loadMine, loadTrades, listedUnread: unread, listedPendingTrades: pendingTrades, t } = useApp();
  /*
   * The two unseen numbers, read from the same place the app's own navigation
   * reads them. Named rather than passed around as a pair, so `NAV`'s `count`
   * key stays a word somebody can read instead of an index.
   */
  const counts = { messages: unread, trades: pendingTrades };
  const [shop, setShop] = React.useState(undefined);
  /*
   * The CardTrader connection's warning and Today's count of failed writes,
   * from the same answer as the shop — so both are on the first frame, and
   * nothing arrives a moment later to push the screen down. Null for a shop
   * with no connection.
   */
  const [cardtrader, setCardtrader] = React.useState(null);
  const [demand, setDemand] = React.useState(null);
  const [scope, setScope] = React.useState([]);
  /*
   * The scope on screen, or null until the back office has resolved one. A
   * ref beside the state: the arrival effect below compares against it, and
   * must not re-run for every change it makes itself.
   */
  const shown = React.useRef(null);
  const acting = !!(trader && trader.kind === 'shop');
  /* The games the address names — none for an absent key, which is All games here. */
  const addressed = (route.game || []).filter((g) => KNOWN_GAMES.has(g)).join(',');

  /*
   * WHAT IS ON SCREEN, which is one of three things: a section of the back
   * office, one of the few app screens a shop is allowed, or — for anything
   * else — the counter, because the shop had no business being there.
   */
  const embedded = EMBEDDED[route.name];
  const section = embedded
    ? ((embedded.secLights || []).includes(route.sec) ? route.sec : embedded.under)
    : (NAV.some((n) => n.value === route.sec) ? route.sec : 'today');

  /*
   * SENT BACK, rather than shown an empty frame. Browse, nearby, trades and the
   * profile are not a shop's screens; arriving on one by a typed address, an
   * old tab or a link in an email lands at the counter instead.
   *
   * In an effect, not during render: navigating IS a state change, and doing it
   * mid-render is how a render loop starts. The same reasoning as the guest
   * redirect in App.jsx, which this sits beside.
   */
  const strayed = !embedded && route.name !== 'dashboard';
  /*
   * AN OLD `/trades` ADDRESS — a bookmark, a link from before ticket 09, an
   * email — lands in Requests rather than at the counter's front, keeping the
   * trade it named and, roughly, its tab: Past stays Past; Active and
   * Proposed open Waiting on you, which is where an accepted trade still to
   * be marked done and a request to answer both are now. With an id the
   * trade opens whichever tab is lit. Replaced, so Back does not return to
   * an address that only bounces.
   */
  const fromTrades = route.name === 'trades'
    /* The address's own games, if it named any: the scope is not resolved yet on this first commit. */
    ? { sec: 'requests', id: route.id, tab: route.tab === 'past' ? 'past' : undefined, game: route.game }
    : null;
  /*
   * And a `sec` that names a rail item with a route of its own goes to that
   * route. `?sec=inbox` is a reasonable thing for somebody to type after
   * seeing `?sec=stock` work.
   */
  const secRoute = !embedded && route.name === 'dashboard'
    ? (NAV.find((n) => n.value === route.sec && n.route) || {}).route
    : undefined;
  React.useEffect(() => {
    if (!acting) return;
    if (fromTrades) go('dashboard', fromTrades, { replace: true });
    else if (strayed) go('dashboard', { replace: true });
    else if (secRoute) go(secRoute, { replace: true });
  }, [acting, strayed, secRoute, route.name]);

  React.useEffect(() => {
    let live = true;
    api.myShop().then((r) => {
      if (!live) return;
      setShop(r.shop);
      setCardtrader(r.cardtrader || null);
    }).catch(() => { if (live) setShop(null); });
    return () => { live = false; };
  }, []);

  /*
   * RE-ASKED WHENEVER THE SCOPE CHANGES, keyed on the scope's value rather
   * than on the array holding it, so an arrival that sets the same games does
   * not ask twice. The server narrows (see the route) by the same games the
   * stock reload sends, so Today and Stock cannot disagree. The last answer
   * stays on screen, marked waiting, until the next lands; one that lands
   * after a newer question was asked is dropped.
   *
   * NOT BEFORE THE SCOPE IS RESOLVED. This effect runs before the arrival
   * effect below, which is the one that reads the address and the device;
   * unguarded, the first render asked for the whole counter and the resolved
   * scope asked again a tick later — two requests, the first of them for
   * numbers nobody was going to see. `resolved` is state rather than the
   * `shown` ref so that a resolved scope of All games, whose key is the same
   * empty string as the unresolved one, still re-runs this effect once.
   */
  const [demandWaiting, setDemandWaiting] = React.useState(false);
  const [resolved, setResolved] = React.useState(false);
  const scopeKey = scope.join(',');
  React.useEffect(() => {
    if (!acting || !resolved) return undefined;
    let live = true;
    setDemandWaiting(true);
    api.shopDemand(scope)
      .then((r) => { if (live) setDemand(r); })
      .catch(() => undefined)
      .then(() => { if (live) setDemandWaiting(false); });
    return () => { live = false; };
  }, [acting, resolved, scopeKey]);

  /*
   * ON ARRIVAL: the address if it names a game, else what this device
   * remembers for this shop, else All games — and the FIRST stock request
   * carries it, so the table is never drawn whole and then narrowed.
   *
   * Afterwards, the address whenever it changes underneath, which is Back
   * and Forward through the sections: an address that names a game is what
   * the screen shows. One that names none leaves the screen as it is — the
   * section moved, the scope did not.
   */
  /*
   * `trader.id` is read and not listed: a change of shop is a page load
   * (`actAs` assigns the location), so within one life of this component it
   * cannot change, and listing it would only promise a re-run that cannot
   * happen.
   */
  React.useEffect(() => {
    if (!acting) return;
    const games = addressed ? addressed.split(',') : (shown.current || rememberedScope(trader.id) || []);
    if (shown.current && shown.current.join() === games.join()) return;
    shown.current = games;
    /*
     * A LINK TEACHES THE DEVICE (the owner, 2026-10-14). Whatever the back
     * office last showed is what this device remembers: a bookmark to the
     * Pokémon stock, worked all afternoon, reopens on Pokémon tomorrow rather
     * than on the Magic of the day before. The link still wins over the memory
     * on arrival; it just becomes the memory as well.
     */
    if (addressed) rememberScope(trader.id, games);
    setScope(games);
    setResolved(true);
    /*
     * With the Stock table's own view where the link names one, so a reload of
     * a searched, sorted table is ONE request already carrying it. Only on
     * arrival: after that the table's controls say it themselves.
     */
    const stock = stockFromLink(route);
    loadMine({ games, q: stock.card || stock.q, synced: stock.synced, sort: stock.sort });
    /*
     * The requests too, narrowed by the same games (see the route): the
     * rail's badge and Today's "Waiting on you" are counted off the list this
     * loads, so scoped to Magic a Pokémon request is neither listed nor
     * counted. The first request on arrival carries the scope, like the stock's.
     */
    loadTrades({ games, open: true });
  }, [acting, addressed]);

  const applyScope = (games) => {
    shown.current = games;
    setScope(games);
    loadMine({ games });
    loadTrades({ games, open: true });
    rememberScope(trader.id, games);
    /*
     * Into the address the shop is at — a section, or a screen opened under
     * one — and REPLACED rather than pushed, as browse writes its filters:
     * Back is for leaving a section, not for stepping back through chips.
     */
    const { name, ...rest } = route;
    go(name, { ...rest, game: scopeParam(games) }, { replace: true });
  };

  if (shop === undefined) return null;

  if (!shop || !acting) {
    /*
     * Not the shop, or no shop at all. Rendered plainly rather than inside the
     * rail: a dark back office around "you have no shop" is furniture for
     * something that is not there.
     */
    return (
      <div className="cbm-shell"><main className="cbm-main">
        <SectionHead title={shop ? shop.name : t("Your shop")}
          sub={shop
            ? t("You are signed in as yourself. Work as the shop to see its stock and its requests.")
            : t("You have no shop on this account yet.")} />
        {shop
          /*
            BACK TO THE ADDRESS THAT WAS ASKED FOR, section and trade included:
            a push about a trade can land on a device that has since gone back
            to being its person, and "work as the shop" must then open that
            trade, not the shop's front.
          */
          ? <Button variant="secondary" iconLeft="store" onClick={() => actAs(shop.id,
              window.location.pathname.startsWith('/shop/dashboard') ? window.location.pathname + window.location.search : '/shop/dashboard')}>
              {t("Work as {name}", { name: shop.name })}
            </Button>
          : <Button onClick={() => go('shop')}>{t("Open a store account")}</Button>}
      </main></div>
    );
  }

  /*
   * `go.to` moves between the rail's sections; `go.open` opens one of the app
   * screens a shop is allowed — which still renders INSIDE the rail, so the
   * name is "open", not "leave". Nothing here leaves.
   */
  /* The scope travels with the section, so the address stays one somebody can send. */
  const to = (value, extra = {}, options) => go('dashboard', { sec: value, game: scopeParam(scope), ...extra }, options);
  const nav = { open: go, to };
  /*
   * `today` is the fallback rather than a crash. `inbox` is a rail item that
   * names a ROUTE, so it never reaches here — but `?sec=inbox` is an address
   * somebody can type, and a section table with a hole in it turns that into a
   * blank screen with an error in the console. See the effect above, which
   * sends it to the real inbox instead.
   */
  const Section = { today: ShopToday, stock: ShopStock, requests: ShopRequests, shop: ShopPage }[section] || ShopToday;

  /*
   * An embedded screen is the ordinary one, unchanged — the same posting form
   * and the same inbox everybody else uses. What changes is the furniture
   * around it, which is the point: a shop posting a card is still posting a
   * card, it simply never leaves its counter to do it.
   */
  const Embedded = embedded ? embedded.Screen : null;
  /*
   * THE ONE THING THE SCOPE HANDS A SCREEN: the posting form — the screen the
   * table marks `takesGame` — opens on the game in scope when the scope names
   * exactly one: an initial value the seller changes, the owner's decision of
   * 2026-10-13. A prop from the shell rather than a key in the address,
   * because the address is also where a trader's form stands and a trader has
   * no scope: the form reads nothing from the address, and App.jsx renders it
   * with no game, so a trader's form cannot default by accident. The other
   * screens are handed nothing at all, and the form nothing under All games
   * or several — a guess between two games is a coin toss in a default's
   * clothes.
   */
  const embeddedProps = embedded && embedded.takesGame
    ? { initialGame: scope.length === 1 ? scope[0] : undefined }
    : {};

  return (
    <div className="sh">
      <aside className="sh-side cbm-wide">
        <Wordmark size={15} tone="inverse" />
        <div className="sh-side__shop">
          <span className="sh-side__label">{t("Shop account")}</span>
          <span className="sh-side__name">{shop.name}</span>
        </div>
        <nav className="sh-side__nav">
          {NAV.map((n) => {
            const count = n.count ? counts[n.count] : 0;
            return (
              <button key={n.value} type="button" data-on={section === n.value ? '1' : '0'}
                      onClick={() => (n.route ? go(n.route) : to(n.value))}>
                <Icon name={n.icon} size={18} /><span>{t(n.label)}</span>
                {/*
                  ANNOUNCED, not just drawn. A bare number beside "Inbox" reads
                  as "Inbox 3" to a screen reader, which is ambiguous enough to
                  be useless; the label says what the three are.
                */}
                {count ? (
                  <span className="sh-side__count" aria-label={n.count === 'messages'
                    ? t("{n} unread", { n: count })
                    : t("{n} waiting on you", { n: count })}>
                    {count > 99 ? '99+' : count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <FluteDivider tone="brand" />
          {/*
            WHO IS AT THE KEYBOARD, which is not the shop. `actingFor` is the
            person; without it this rail would say the shop's name twice and
            never the name of whoever is standing behind the counter.
          */}
          {trader.actingFor ? (
            <div className="sh-side__me">
              <span className="cbm-friend__init" style={{ background: 'var(--ink-800)', borderColor: 'var(--ink-700)', color: 'var(--kraft-50)' }}>
                {trader.actingFor.displayName.slice(0, 1).toUpperCase()}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)', color: 'var(--kraft-50)' }}>
                  {trader.actingFor.displayName}
                </div>
                <div className="sh-side__meta">{t("Behind the counter")}</div>
              </div>
            </div>
          ) : null}
          {/*
            THE LANGUAGE, WHICH A SEALED BACK OFFICE HAD NO WAY TO CHANGE.
            Reported: "the language settings are missing in the shop dashboard."
            Right — the ordinary header carries this switch and a shop never
            sees that header, so a shopkeeper reading the wrong language had
            nowhere to go. Settings is not the answer either: that screen is
            behind the trading side, which is exactly what this account is
            sealed out of.

            Beside the way back rather than in a section, for the same reason it
            sits in the header everywhere else — somebody reading a language they
            do not speak is the person least able to hunt for the control.
          */}
          <div style={{ padding: '0 var(--space-4) var(--space-3)' }}>
            <LanguageSwitch />
          </div>
          <button type="button" className="sh-side__switch" onClick={() => actAs(null, '/')}>
            <Icon name="arrow-left" size={16} />{t("Switch to trading")}
          </button>
        </div>
      </aside>

      <div className="sh-main">
        <header className="sh-top">
          <div className="cbm-narrow" style={{ minWidth: 0 }}>
            <span className="sh-top__label">{t("Shop account")}</span>
            <div className="sh-top__name">{shop.name}</div>
          </div>
          {/* Compact here: the top bar already carries the shop's name and the
              way back, and the compact form shows the language you would GET
              rather than the one you are in. */}
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }} className="cbm-narrow">
            <LanguageSwitch compact />
            <Button size="sm" variant="outline" iconLeft="arrow-left" onClick={() => actAs(null, '/')}>
              {t("Trading")}
            </Button>
          </span>
        </header>

        {/* Not on the shop's own page, where a scope over an address is noise; nor on a screen the table says shows no bar (the import — see `EMBEDDED`). */}
        {section === 'shop' || (embedded && embedded.bar === false) ? null : <ScopeBar scope={scope} onScope={applyScope} />}

        <main className="sh-content">
          <ConnectionWarning warning={cardtrader && cardtrader.warning} go={go} />
          {Embedded
            ? <Embedded {...embeddedProps} />
            : <Section shop={shop} demand={demand} demandWaiting={demandWaiting} route={route} go={nav} scope={scope}
                       cardtrader={cardtrader} />}
        </main>
      </div>

      <div className="cbm-narrow" style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 30 }}>
        <BottomNav
          value={section}
          onChange={(v) => {
            const item = NAV.find((n) => n.value === v);
            if (item && item.route) go(item.route); else to(v);
          }}
          items={NAV.map((n) => ({
            value: n.value, label: t(n.label), icon: n.icon,
            badge: (n.count ? counts[n.count] : 0) || undefined,
          }))}
        />
      </div>
    </div>
  );
}
